import type { ThemeColor } from '@shared/types/theme';
import type { GoogleAdsCheckKind } from '@shared/google-ads-check';
import type { JobRunSummary } from '@shared/types/jobs';
import type { SyncRange } from './sync-in-weeks';
import type { LabelPageLayoutSettings } from '@shared/types/label-page-layout';
import type { OverstockSettings } from '@shared/overstock-settings';
import {
  DEFAULT_XERO_SALES_EXPORT_SETTINGS,
  type ShopifySalesXeroReportResponse,
  type XeroSalesExportSettings,
} from '@shared/types/xero-sales-export';
import type { XeroInvoicePostingSettings } from '@shared/types/xero-invoice-posting';
import type { XeroPurchasesSettings } from '@shared/types/xero-purchases';
import type { ReceivingReviewFreightOption } from '@shared/types/receiving';
import type {
  DiscardProductContentResponse,
  ProductContentCommitStartResponse,
  ProductContentCommitStatusResponse,
  ProductContentDraftResponse,
  ProductContentDraftSummaryResponse,
  StageCollectionContentRequest,
  StageCollectionContentResponse,
  StageProductDescriptionRequest,
  StageProductDescriptionResponse,
  StageProductSeoRequest,
  StageProductSeoResponse,
  StageProductTeaserRequest,
  StageProductTeaserResponse,
  StageProductTypeRequest,
  StageProductTypeResponse,
  CollectionContentPayload,
  ProductContentPendingCollectionContent,
} from '@shared/types/product-content';
import { useAuthStore } from '@/stores/authStore';
import { redirectToLoginForExpiredSession, redirectToSelectTeam } from '@/lib/auth-redirect';
import { preferredTeamForThisTab } from '@/lib/tab-team';
import { beginTeamExit } from '@/utils/team-exit';
import {
  NETWORK_ERROR_TEXT,
  isSessionGone,
  noResponse,
  toApiResponse,
  type ApiResponse,
} from '@/lib/api-contract';
import {
  MARKETING_BACKFILL_DEFAULT_DAYS,
  INSTAGRAM_BACKFILL_DEFAULT_DAYS,
  FACEBOOK_BACKFILL_DEFAULT_DAYS,
} from '@shared/constants';

export type { ApiResponse } from '@/lib/api-contract';

// Auth response types
interface AuthResponse {
  token: string;
  pending_invite_token?: string | null;
  user: {
    id: string;
    email: string;
    displayname?: string;
    photourl?: string;
    team_displayname?: string;
    role: string;
    teams?: Array<{
      id: string;
      displayname: string;
      shopify_store_domain: string;
      status?: string;
    }>;
    active_team?: {
      id: string;
      displayname: string;
      shopify_store_domain: string;
      status?: string;
      settings?: Record<string, unknown>;
      xero_tenant_id?: string;
      xero_tenant_name?: string;
      xero_basecurrency?: string;
      xero_shortcode?: string;
    };
  };
}

const isDev = () => process.env.NODE_ENV === 'development';

/** A 401 from these is the answer itself, never a reason to renew the session. */
const AUTH_BOOTSTRAP_PATHS = ['/auth/session', '/auth/refresh'];

/**
 * Every API call goes through here. It never throws: failures come back with `status` (0 when no
 * answer arrived), `code` and a readable `error`. An expired token is renewed once, shared by every
 * request that hit it, and the request is retried once. Only /auth/refresh answering 401 or 403
 * signs the person out; a network or server failure keeps them signed in (decision 11 a).
 */
export async function fetchApi<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
  const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return request<T>(path, options, false);
}

async function request<T>(path: string, options: RequestInit, retried: boolean): Promise<ApiResponse<T>> {
  const { res, sentToken } = await sendOnce<T>(path, options);
  if (res.status === 401 && !retried && !AUTH_BOOTSTRAP_PATHS.some((p) => path.startsWith(p))) {
    return renewAndRetry(path, options, res, sentToken);
  }
  // A token with no team on a team route: choose a team first (T-153).
  if (res.status === 403 && res.code === 'NO_ACTIVE_TEAM') redirectToSelectTeam();
  return res;
}

/** One request and its answer. Records the bearer actually sent, so a 401 knows what went stale. */
async function sendOnce<T>(
  path: string,
  options: RequestInit
): Promise<{ res: ApiResponse<T>; sentToken: string | null }> {
  const method = options.method ?? 'GET';
  // FormData gets no Content-Type, so the browser sets the multipart boundary.
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
  const headers: Record<string, string> = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(options.headers as Record<string, string> | undefined),
  };
  if (isFormData) {
    delete headers['Content-Type'];
    delete headers['content-type'];
  }
  // A caller's own Authorization is kept; otherwise this tab's token.
  const storeToken = useAuthStore.getState().user?.token;
  if (!headers.Authorization && !headers.authorization && storeToken) {
    headers.Authorization = `Bearer ${storeToken}`;
  }
  const bearer = headers.Authorization ?? headers.authorization;
  const sentToken = bearer?.startsWith('Bearer ') ? bearer.slice('Bearer '.length) : null;

  let res: ApiResponse<T>;
  try {
    // eslint-disable-next-line no-restricted-syntax -- fetchApi itself.
    const response = await fetch(`/api${path}`, {
      ...options,
      credentials: options.credentials ?? 'include',
      headers,
    });
    const text = await response.text();
    res = toApiResponse<T>(response.status, text);
    if (isDev() && text && res.data === undefined && res.body === undefined) {
      console.warn(`[API] ${method} ${path} answered with something other than JSON:`, text.slice(0, 200));
    }
  } catch (e) {
    res = noResponse<T>(e);
  }
  logResult(method, path, res);
  return { res, sentToken };
}

/** Dev: one line per request. Production: only failures with no answer or a server error. */
function logResult(method: string, path: string, res: ApiResponse<unknown>) {
  if (isDev()) {
    console.log(`[API] ${method} ${path} → ${res.status}${res.code ? ` ${res.code}` : ''}`);
  } else if (res.status === 0 || res.status >= 500) {
    // No body, headers or query string: they can carry tokens and customer data.
    console.warn(`[API] ${method} ${path.split('?')[0]} → ${res.status}${res.code ? ` ${res.code}` : ''}`);
  }
}

function sessionEnded<T>(): ApiResponse<T> {
  redirectToLoginForExpiredSession();
  return { status: 401, code: 'SESSION_EXPIRED', error: 'Your session has ended. Sign in again.' };
}

/** A 401 on a first attempt: renew the session (or use one another request renewed) and retry once. */
async function renewAndRetry<T>(
  path: string,
  options: RequestInit,
  unauthorized: ApiResponse<T>,
  sentToken: string | null
): Promise<ApiResponse<T>> {
  const storeToken = useAuthStore.getState().user?.token;
  if (!storeToken) return sessionEnded();

  let token = storeToken;
  if (storeToken === sentToken) {
    const outcome = await refreshSessionOnce(storeToken);
    if (outcome.kind === 'signed-out') return sessionEnded();
    if (outcome.kind === 'failed') {
      useAuthStore.getState().setSessionRefreshFailed(true);
      if (outcome.status === 0) return { status: 0, code: 'NETWORK', error: NETWORK_ERROR_TEXT };
      return {
        status: outcome.status,
        code: 'SESSION_REFRESH_FAILED',
        error: "jflo couldn't renew your session. Try again in a moment.",
      };
    }
    token = outcome.token;
  }
  // The token changed under this request: a team switch without a reload must never replay it
  // against the other team.
  if (!sameTeamClaim(token, sentToken)) return unauthorized;
  if (options.signal?.aborted) return noResponse<T>(new DOMException('Aborted', 'AbortError'));

  const headers = { ...(options.headers as Record<string, string> | undefined) };
  delete headers.authorization;
  headers.Authorization = `Bearer ${token}`;
  return request<T>(path, { ...options, headers }, true);
}

/** The token's active_team_id claim: null when it has none, undefined when it can't be read. */
function tokenTeamClaim(token: string | null): string | null | undefined {
  const payload = token?.split('.')[1];
  if (!payload) return undefined;
  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const claims = JSON.parse(atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '='))) as {
      active_team_id?: unknown;
    };
    return typeof claims.active_team_id === 'string' ? claims.active_team_id : null;
  } catch {
    return undefined;
  }
}

function sameTeamClaim(a: string | null, b: string | null): boolean {
  const teamA = tokenTeamClaim(a);
  const teamB = tokenTeamClaim(b);
  return teamA === undefined || teamB === undefined || teamA === teamB;
}

type RefreshOutcome =
  | { kind: 'ok'; token: string }
  | { kind: 'signed-out'; status: number }
  | { kind: 'failed'; status: number };

/** One renewal per tab at a time: every request that hit the same stale token waits for it. */
let refreshInFlight: { from: string; promise: Promise<RefreshOutcome> } | null = null;

function refreshSessionOnce(stale: string): Promise<RefreshOutcome> {
  if (refreshInFlight?.from === stale) return refreshInFlight.promise;
  const entry = { from: stale, promise: runRefresh(stale) };
  refreshInFlight = entry;
  void entry.promise.finally(() => {
    if (refreshInFlight === entry) refreshInFlight = null;
  });
  return entry.promise;
}

async function runRefresh(stale: string): Promise<RefreshOutcome> {
  const res = await refreshToken(stale);
  if (res.data?.token) {
    const store = useAuthStore.getState();
    // A team switch made while this ran wins (T-155): keep its token, not the renewed one.
    if (store.user?.token === stale) {
      const before = tokenTeamClaim(stale);
      const next = res.data.user?.active_team;
      if (typeof before === 'string' && next?.id && next.id !== before) {
        // Removed from this tab's team, so the server chose another: close the old team's pages
        // with the old token, then reload in the new team. Not announced to other tabs (silent).
        await beginTeamExit(
          `You're no longer in ${store.user?.active_team?.displayname ?? 'that team'}. Opening ${next.displayname}…`
        );
        if (useAuthStore.getState().user?.token === stale) useAuthStore.getState().updateUserAndToken(res.data);
        // Reload either way: the team pages are gone and the guards silenced.
        window.location.assign('/');
        // The token's team differs from every waiting request's, so none is replayed.
        return { kind: 'ok', token: res.data.token };
      }
      store.updateUserAndToken(res.data);
    }
    const current = useAuthStore.getState().user?.token;
    return current ? { kind: 'ok', token: current } : { kind: 'signed-out', status: 401 };
  }
  if (isSessionGone(res.status)) return { kind: 'signed-out', status: res.status };
  // A 2xx with no token is a broken answer, not a signed-out person.
  return { kind: 'failed', status: res.status >= 200 && res.status < 300 ? 502 : res.status };
}

/** Swap this tab's token for a fresh one. Only fetchApi calls it: a 401 means renew, then retry. */
async function refreshToken(token: string) {
  return fetchApi<AuthResponse>('/auth/refresh', {
    method: 'POST',
    body: JSON.stringify({ token }),
    credentials: 'include',
  });
}

/** Retry after a failed renewal (the "Can't reach jflo" banner). Signs out only on 401 or 403. */
export async function retrySessionRefresh(): Promise<RefreshOutcome['kind']> {
  const token = useAuthStore.getState().user?.token;
  if (!token) {
    redirectToLoginForExpiredSession();
    return 'signed-out';
  }
  const outcome = await refreshSessionOnce(token);
  if (outcome.kind === 'signed-out') redirectToLoginForExpiredSession();
  // updateUserAndToken clears the flag, unless a team switch kept its own token meanwhile.
  if (outcome.kind === 'ok') useAuthStore.getState().setSessionRefreshFailed(false);
  return outcome.kind;
}

/** This tab's team, or for a brand-new tab the team of the tab used last (T-155). */
function getLastActiveTeamId(): string | null {
  return preferredTeamForThisTab(useAuthStore.getState().user?.active_team?.id);
}

/** Exchange a BetterAuth session cookie for the app JWT + user payload (same-origin cookies). */
export async function exchangeBetterAuthSession() {
  const preferredTeamId = getLastActiveTeamId();
  return fetchApi<AuthResponse>('/auth/session', {
    method: 'POST',
    body: JSON.stringify(
      preferredTeamId ? { preferred_team_id: preferredTeamId } : {},
    ),
  });
}

// Link a Shopify store to the user's account
export async function linkShopifyStore(options: {
  storeDomain: string;
  accessToken?: string;
  clientId?: string;
  clientSecret?: string;
}) {
  const body: Record<string, string> = {
    shop_domain: options.storeDomain,
  };
  if (options.accessToken?.trim()) {
    body.access_token = options.accessToken.trim();
  }
  if (options.clientId?.trim() && options.clientSecret?.trim()) {
    body.client_id = options.clientId.trim();
    body.client_secret = options.clientSecret.trim();
  }

  return fetchApi<AuthResponse>('/auth/link-shopify', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function getShopifyOAuthConfig() {
  return fetchApi<{ appCredentialsConfigured: boolean; redirectUri: string }>(
    '/auth/shopify/oauth/config',
    { method: 'GET' },
  );
}

export async function startShopifyOAuth(options: {
  storeDomain: string;
  clientId?: string;
  clientSecret?: string;
}) {
  const body: Record<string, string> = {
    shop_domain: options.storeDomain,
  };
  if (options.clientId?.trim()) {
    body.client_id = options.clientId.trim();
  }
  if (options.clientSecret?.trim()) {
    body.client_secret = options.clientSecret.trim();
  }

  return fetchApi<{ authUrl: string; redirectUri: string; shop: string }>(
    '/auth/shopify/oauth/start',
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  );
}


export async function completeShopifyOAuth(queryParams: Record<string, string>) {
  return fetchApi<AuthResponse>('/auth/shopify/oauth/callback', {
    method: 'POST',
    body: JSON.stringify(queryParams),
  });
}

// Set the active team
export async function setActiveTeam(teamId: string) {
  return fetchApi<AuthResponse>('/auth/set-active-team', {
    method: 'POST',
    // No manual headers needed
    body: JSON.stringify({ team_id: teamId }),
  });
}

export interface TeamSettingsPayload {
  numberFormat?: string;
  currency?: string;
  timezone?: string;
  currencyDisplay?: 'symbol' | 'code' | 'name' | 'narrowSymbol';
  themeColor?: ThemeColor;
  /** When false, the daily cron skips this team. Manual sync still works. Default true. */
  automatedSyncEnabled?: boolean;
  xeroTransactionSyncStartDate?: string | null;
  xeroSalesExport?: XeroSalesExportSettings | null;
  xeroInvoicePosting?: XeroInvoicePostingSettings | null;
  xeroPurchases?: XeroPurchasesSettings | null;
  /** Labels wizard Step 4 page layout defaults. */
  labelsPageLayout?: LabelPageLayoutSettings | null;
  /** The Overstock report's defaults (Team settings card). */
  overstock?: OverstockSettings;
  /** The message a purchase order email starts with; '' means the default (T-134). */
  purchaseOrderEmail?: { message: string };
  /** Where the Chrome extension counts time (T-77); absent means the default list. */
  timeTrackingSites?: string[];
}

export type { ShopifySalesXeroReportResponse, XeroSalesExportSettings };
export { DEFAULT_XERO_SALES_EXPORT_SETTINGS };
export type {
  XeroInvoicePostingSettings,
  XeroInvoicePostStatusResponse,
  XeroInvoicePostStatusRow,
} from '@shared/types/xero-invoice-posting';
export { DEFAULT_XERO_INVOICE_POSTING_SETTINGS } from '@shared/types/xero-invoice-posting';
export type { XeroPurchasesSettings } from '@shared/types/xero-purchases';
export { EMPTY_XERO_PURCHASES_SETTINGS } from '@shared/types/xero-purchases';

export async function getXeroPostingEligibleCount(postingStartDate: string) {
  return fetchApi<{ count: number }>(
    `/xero-posting/eligible-count?postingStartDate=${encodeURIComponent(postingStartDate)}`
  );
}

export async function getXeroPostingPaymentMethods() {
  return fetchApi<{ paymentMethods: string[] }>('/xero-posting/payment-methods');
}

export async function getXeroPostingPaymentAccounts() {
  return fetchApi<{ accounts: Array<{ code: string; name: string; type: string | null }> }>(
    '/xero-posting/payment-accounts'
  );
}

/** Expense / direct-cost accounts for purchases freight defaults (same set as Review bill). */
export async function getXeroExpenseAccounts() {
  return fetchApi<{ accounts: ReceivingReviewFreightOption[] }>(
    '/xero-posting/expense-accounts'
  );
}

export async function getXeroPostingBrandingThemes() {
  return fetchApi<{
    brandingThemes: Array<{ id: string; name: string; sortOrder: number | null }>;
  }>('/xero-posting/branding-themes');
}

export type XeroTrackingOption = {
  id: string;
  name: string;
  status: string | null;
};

export type XeroTrackingCategory = {
  id: string;
  name: string;
  status: string | null;
  options: XeroTrackingOption[];
};

export async function getXeroTrackingCategories() {
  return fetchApi<{ trackingCategories: XeroTrackingCategory[] }>(
    '/xero-posting/tracking-categories'
  );
}

export async function getXeroPostingShopifyLocations() {
  return fetchApi<{
    locations: Array<{ id: string; name: string; isPrimary: boolean; isActive: boolean }>;
    hasInventoryWriteScope?: boolean;
    hasReadLocationsScope?: boolean;
    reconnectRequiredForStockSync?: boolean;
    error?: string;
  }>('/xero-posting/shopify-locations');
}

export type XeroTaxTypeOption = {
  code: string;
  name: string;
  status: string | null;
  canApplyToRevenue: boolean | null;
  canApplyToExpenses: boolean | null;
  displayTaxRate: string | null;
};

export async function getXeroTaxTypes() {
  return fetchApi<{ taxTypes: XeroTaxTypeOption[] }>('/xero-posting/tax-types');
}

export async function getProductTypes() {
  return fetchApi<{
    productTypes: string[];
    types?: import('@shared/types/product-type-catalog').CatalogProductType[];
  }>('/products/product-types');
}

export { MEDIA_CONTENT_TYPES, type MediaContentType } from '@shared/types/product-media';

export type MediaQueryParams = {
  page?: number;
  limit?: number;
  query?: string;
  /** Brand ids. */
  vendorIds?: string[];
  /** Brand names, from links made before brands were ids (`shared/brand-filter.ts`). */
  vendors?: string[];
  contentTypes?: string[];
  /** Mime types, e.g. image/png. */
  fileTypes?: string[];
  productStatuses?: string[];
  minFileSizeKb?: number;
  maxFileSizeKb?: number;
  minShortestSide?: number;
  maxShortestSide?: number;
  nonSquareOnly?: boolean;
  /** Alt text statuses (T-165); images only. */
  altStatuses?: string[];
  sortField?: string;
  sortDirection?: 'ascending' | 'descending';
};

export type MediaListItem = {
  id: string;
  shopify_media_refid: string;
  media_content_type: string | null;
  status: string | null;
  alt_text: string | null;
  image_url: string | null;
  preview_image_url: string | null;
  width: number | null;
  height: number | null;
  file_size: number | null;
  mime_type: string | null;
  format: string | null;
  filename: string | null;
  duration_ms: number | null;
  embed_url: string | null;
  origin_url: string | null;
  host: string | null;
  shopify_image_refid: string | null;
  product_position: number | null;
  shopify_file_status: string | null;
  shopify_created_at: string | null;
  shopify_updated_at: string | null;
  variant_assignment_count: number;
  product_id: string;
  product_refid: string | null;
  product_title: string | null;
  product_status: string | null;
  vendor_name: string;
  /** Where the image's alt text stands (T-165); null for anything but an image. */
  alt_status: import('@shared/product-image-alt-text').AltTextStatus | null;
  /** jflo's suggestion, waiting for review or last written. */
  alt_suggested: string | null;
  /** Why the last save or suggestion failed. */
  alt_error: string | null;
};

export type MediaListResponse = {
  media: MediaListItem[];
  total: number;
  page: number;
  limit: number;
};

export async function getMedia(params: MediaQueryParams = {}) {
  const qs = new URLSearchParams();
  if (params.page != null) qs.set('page', String(params.page));
  if (params.limit != null) qs.set('limit', String(params.limit));
  if (params.query) qs.set('query', params.query);
  if (params.vendorIds?.length) qs.set('vendorIds', params.vendorIds.join(','));
  if (params.vendors?.length) qs.set('vendors', params.vendors.join(','));
  if (params.contentTypes?.length) {
    qs.set('contentTypes', params.contentTypes.join(','));
  }
  if (params.fileTypes?.length) qs.set('fileTypes', params.fileTypes.join(','));
  if (params.productStatuses?.length) {
    qs.set('productStatuses', params.productStatuses.join(','));
  }
  if (params.minFileSizeKb != null && params.minFileSizeKb > 0) {
    qs.set('minFileSizeKb', String(params.minFileSizeKb));
  }
  if (params.maxFileSizeKb != null && params.maxFileSizeKb < 51200) {
    qs.set('maxFileSizeKb', String(params.maxFileSizeKb));
  }
  if (params.minShortestSide != null && params.minShortestSide > 0) {
    qs.set('minShortestSide', String(params.minShortestSide));
  }
  if (params.maxShortestSide != null && params.maxShortestSide < 10000) {
    qs.set('maxShortestSide', String(params.maxShortestSide));
  }
  if (params.nonSquareOnly === true) {
    qs.set('nonSquareOnly', 'true');
  }
  if (params.altStatuses?.length) qs.set('altStatuses', params.altStatuses.join(','));
  if (params.sortField) qs.set('sortField', params.sortField);
  if (params.sortDirection) qs.set('sortDirection', params.sortDirection);
  const query = qs.toString();
  return fetchApi<MediaListResponse>(`/media${query ? `?${query}` : ''}`, {
    method: 'GET',
  });
}

// Get variant details
export async function getVariantDetails(variantId: string) {
  return fetchApi<import('@shared/types/variant-details').VariantDetailsResponse>(
    `/products/variant/${variantId}`,
    {
      // No manual headers needed
    }
  );
}

// Get Shopify product details (+ child variants)
export async function getProductDetails(productId: string) {
  return fetchApi<import('@shared/types/product-details').ProductDetailsResponse>(
    `/products/product/${productId}`
  );
}

/** The product's SEO title and description, and any change waiting in the Content queue (T-473). */
export async function getProductSeo(productId: string, options: { signal?: AbortSignal } = {}) {
  return fetchApi<import('@shared/types/product-content').ProductSeoResponse>(
    `/products/product/${encodeURIComponent(productId)}/seo`,
    { signal: options.signal }
  );
}

/** The product page's Google searches (T-27): its Searches card, and the marks in its editors. */
export async function getProductSearchTerms(productId: string, options: { signal?: AbortSignal } = {}) {
  return fetchApi<import('@shared/types/ai').CollectionSearchTermsResponse>(
    `/products/product/${encodeURIComponent(productId)}/search-terms`,
    { signal: options.signal }
  );
}

export type XeroVariantStatus = 'active' | 'inactive' | 'archived';

export async function updateVariantXeroStatus(
  variantId: string,
  payload: { xero_status: XeroVariantStatus } | { reset_to_sync: true }
) {
  return fetchApi<{ variant: { variant_id: string; xero_status: string; xero_status_manual: boolean } }>(
    `/products/variant/${variantId}/xero-status`,
    {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }
  );
}

export type ShopifyVariantStatus =
  | 'ACTIVE'
  | 'ARCHIVED'
  | 'SUPPLIER_DISCONTINUING'
  | 'DERANGING';

export async function updateVariantShopifyStatus(
  variantId: string,
  payload: { shopify_status: ShopifyVariantStatus } | { reset_to_sync: true }
) {
  return fetchApi<{
    variant: { variant_id: string; shopify_status: string; shopify_status_manual: boolean };
  }>(`/products/variant/${variantId}/shopify-status`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export async function previewVariantShopifyStatusLive(
  variantId: string,
  status: ShopifyVariantStatus
) {
  return fetchApi<import('@shared/types/shopify-status-live').ShopifyStatusLivePreview>(
    `/products/variant/${variantId}/shopify-status-live-preview?status=${encodeURIComponent(status)}`
  );
}

export async function pushVariantShopifyStatusLive(
  variantId: string,
  payload: {
    shopify_status: ShopifyVariantStatus;
    acknowledged_warnings?: import('@shared/types/shopify-status-live').ShopifyStatusLiveWarningCode[];
  }
) {
  return fetchApi<import('@shared/types/shopify-status-live').ShopifyStatusLiveSuccess>(
    `/products/variant/${variantId}/shopify-status-live`,
    {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }
  );
}

// Get variant movements
export async function getVariantMovements(variantId: string) {
  return fetchApi<{ movements: any[] }>(`/products/variant/${variantId}/movements`, {
    // No manual headers needed
  });
}

// Get variant retail price history
export async function getVariantPriceHistory(variantId: string) {
  return fetchApi<{ priceHistory: any[] }>(`/products/variant/${variantId}/price-history`, {
    // No manual headers needed
  });
}

export type VariantPriceDailyPoint = {
  date: string;
  retail_price: number | null;
  compare_at_price: number | null;
};

export type VariantSalesDailyPoint = {
  date: string;
  sales_units: number;
  sales_revenue: number;
  sales_cost: number;
};

export type VariantPriceDailyResult = {
  points: VariantPriceDailyPoint[];
  seed: VariantPriceDailyPoint | null;
  sales: VariantSalesDailyPoint[];
  variant: {
    id: string;
    sku: string | null;
    name: string | null;
    product_title: string | null;
  };
  meta: { from: string; to: string };
};

/** Daily retail / compare-at snapshots for a variant over a date range. */
export async function getVariantPriceDaily(
  variantId: string,
  from: string,
  to: string
) {
  const qs = new URLSearchParams({ from, to });
  return fetchApi<VariantPriceDailyResult>(
    `/products/variant/${variantId}/price-daily?${qs.toString()}`,
    { method: 'GET' }
  );
}


// Test Xero integration
export async function testXeroIntegration() {
  return fetchApi<{
    status: string;
    organisation?: any;
    message?: string;
    details?: any;
    hasWriteScope?: boolean;
    reconnectRequiredForPosting?: boolean;
    scopes?: string | null;
  }>('/integrations/test-xero', {
    // No manual headers needed
  });
}

// Test Google Marketing integration
export async function testMicrosoftIntegration() {
  return fetchApi<{
    status: string;
    account?: {
      name: string | null;
      microsoftCustomerId?: string | null;
      microsoftAccountId?: string | null;
      needsMicrosoftAccountIds?: boolean;
      accountsAccessible?: number;
    };
    message?: string;
    details?: any;
  }>('/integrations/test-microsoft', {});
}

export async function testMetaIntegration() {
  return fetchApi<{
    status: string;
    account?: {
      name: string | null;
      metaAdAccountId?: string | null;
      metaAdAccountName?: string | null;
      needsMetaAdAccountSelection?: boolean;
      adAccountsAccessible?: number;
    };
    message?: string;
    details?: any;
  }>('/integrations/test-meta', {});
}

export async function getMetaAdAccounts() {
  return fetchApi<{ accounts: { adAccountId: string; name: string; currency: string | null }[] }>(
    '/integrations/meta-ads/accounts',
    {}
  );
}

export async function saveMetaAdAccount(adAccountId: string, accountName?: string) {
  return fetchApi<{ success: boolean }>('/integrations/meta-ads/account', {
    method: 'POST',
    body: JSON.stringify({ adAccountId, accountName }),
  });
}

export async function getMicrosoftAdsAccounts() {
  return fetchApi<{
    accounts: { customerId: string; accountId: string; name: string }[];
  }>('/integrations/microsoft-ads/accounts', {});
}

export async function saveMicrosoftAdsAccount(
  customerId: string,
  accountId: string,
  accountName?: string
) {
  return fetchApi<{ success: boolean }>('/integrations/microsoft-ads/account', {
    method: 'POST',
    body: JSON.stringify({ customerId, accountId, accountName }),
  });
}

export async function testGoogleIntegration() {
  return fetchApi<{
    status: string;
    account?: {
      name: string | null;
      adsAccountsAccessible?: number;
      adsVerificationWarning?: string;
      googleAdsCustomerId?: string | null;
      googleAdsCustomerName?: string | null;
      needsGoogleAdsCustomerSelection?: boolean;
      ga4PropertyId?: string | null;
      searchConsoleSiteUrl?: string | null;
    };
    message?: string;
    details?: any;
  }>('/integrations/test-google', {});
}

export async function getGoogleGa4Properties() {
  return fetchApi<{
    properties: Array<{
      propertyResource: string;
      displayName: string;
      accountDisplayName: string;
    }>;
  }>('/integrations/google/ga4-properties', {});
}

export async function getGoogleSearchConsoleSites() {
  return fetchApi<{ sites: Array<{ siteUrl: string; permissionLevel?: string }> }>(
    '/integrations/google/search-console-sites',
    {}
  );
}

export async function saveGoogleSeoSettings(body: {
  ga4PropertyId?: string | null;
  searchConsoleSiteUrl?: string | null;
}) {
  return fetchApi<{ success: boolean }>('/integrations/google/seo-settings', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function getGoogleAdsCustomers() {
  return fetchApi<{ customers: { customerId: string; descriptiveName: string }[] }>(
    '/integrations/google-ads/customers',
    {}
  );
}

export async function selectGoogleAdsCustomer(customerId: string, descriptiveName?: string) {
  return fetchApi<{ success: boolean }>('/integrations/google-ads/customer', {
    method: 'POST',
    body: JSON.stringify({ customerId, descriptiveName }),
  });
}

// Interface for workflow history item
export interface WorkflowHistoryItem {
  workflow_id: string;
  created_at: string;
  updated_at: string;
  status: string;
  current_step?: string | null;
  error_message?: string | null;
  kind?: 'main' | 'marketing';
  step_label?: string | null;
}

// Interface for workflow history response
export interface WorkflowHistoryResponse {
  success: boolean;
  data: WorkflowHistoryItem[];
  total: number;
  page: number;
  limit: number;
  active: WorkflowHistoryItem | null;
  lastCompletedMainSyncAt?: string | null;
  /** The run asked for with `follow`: absent when not asked, null when the team has no such run. */
  followed?: WorkflowHistoryItem | null;
}

// Function to trigger a sync
export async function triggerSync() {
  return fetchApi('/sync/trigger', {
    method: 'POST',
    body: JSON.stringify({}),
    // No manual headers needed
    headers: {
      'Content-Type': 'application/json' // Keep content type if needed
    }
  });
}

/** Full product fetch + incremental Shopify collections (by last sync), then local DB sync. */
export async function triggerCatalogSync() {
  return fetchApi<{ success: boolean; message?: string }>('/sync/catalog', {
    method: 'POST',
    body: JSON.stringify({})
  });
}

/** Full product fetch + every Shopify collection + delete local rows for collections no longer in Shopify. */
export async function triggerCatalogFullCollectionSync() {
  return fetchApi<{ success: boolean; message?: string }>('/sync/catalog/full-collections', {
    method: 'POST',
    body: JSON.stringify({})
  });
}

/** Re-fetch specific products from Shopify (same fields as daily sync) and materialize locally. */
export async function refreshProductsFromShopify(productIds: string[]) {
  return fetchApi<{
    success: boolean;
    requested: number;
    resolved: number;
    fetched: number;
    missing_ids: string[];
    message?: string;
  }>('/products/refresh-from-shopify', {
    method: 'POST',
    body: JSON.stringify({ product_ids: productIds }),
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

// Function to trigger a daily totals sync
export async function triggerDailyTotalsSync() {
  return fetchApi('/sync/dailytotals', {
    method: 'POST',
    // No manual headers needed
    body: JSON.stringify({}) // Add empty JSON body
  });
}

// Function to trigger a daily retail price snapshot sync
export async function triggerVariantPriceDailySync() {
  return fetchApi('/sync/variantpricedaily', {
    method: 'POST',
    body: JSON.stringify({})
  });
}

/** A backfill run (job_run), in the job status vocabulary. */
export type MarketingBackfillPresence = JobRunSummary;

export type MarketingDataPresence = {
  rowCount: number;
  minDate: string | null;
  maxDate: string | null;
  lastSyncedAt: string | null;
  /** Latest workflow-backed backfill (Google Ads / Search Console). */
  backfill?: MarketingBackfillPresence | null;
};

export type MarketingSyncStatus = {
  googleAds: MarketingDataPresence;
  microsoftAds: MarketingDataPresence;
  metaAds: MarketingDataPresence;
  ga4: MarketingDataPresence;
  searchConsole: MarketingDataPresence;
  ahrefs: MarketingDataPresence;
  instagram: MarketingDataPresence;
  facebook: MarketingDataPresence;
  shopifyMarketing: MarketingDataPresence;
  /** @deprecated Use shopifyMarketing */
  shopifyEmail: MarketingDataPresence;
  /** Presence min/max dates are not proof a range is complete. */
  datasetCoverage?: {
    path: string;
    note: string;
  };
};

export async function getMarketingSyncStatus() {
  return fetchApi<MarketingSyncStatus>('/marketing/sync-status');
}

export type MarketingBackfillPlatform =
  | 'googleAds'
  | 'searchConsole'
  | 'ga4'
  | 'microsoftAds'
  | 'metaAds'
  | 'instagram'
  | 'facebook'
  | 'shopifyMarketing';

/** Cancels the platform's running backfill for real: its workflow stops and the run reads Cancelled. */
export async function cancelMarketingBackfill(platform: MarketingBackfillPlatform) {
  return fetchApi<{ success: boolean; backfill?: MarketingBackfillPresence | null }>(
    '/marketing/backfill/cancel',
    { method: 'POST', body: JSON.stringify({ platform }) }
  );
}

export async function triggerGoogleAdsSync(startDate?: string, endDate?: string) {
  return fetchApi<{ success: boolean; rowsUpserted: number }>('/marketing/sync/google-ads', {
    method: 'POST',
    body: JSON.stringify({ startDate, endDate }),
  });
}

export async function triggerGoogleAdsBackfill(days: number = MARKETING_BACKFILL_DEFAULT_DAYS) {
  return fetchApi<{ success: boolean; workflowInstanceId: string }>(
    '/marketing/sync/google-ads/backfill',
    {
      method: 'POST',
      body: JSON.stringify({ days }),
    }
  );
}

type WeeklySyncData = { success: boolean; rowsUpserted: number; next: SyncRange | null; weeks: number };

/** One week of GA4 Sync now (`range` null: the default range); see `syncInWeeks`. */
export async function triggerGa4Sync(range: SyncRange | null) {
  return fetchApi<WeeklySyncData>('/marketing/sync/ga4', {
    method: 'POST',
    body: JSON.stringify({ oneWeek: true, ...range }),
  });
}

export async function triggerGa4Backfill(days: number = MARKETING_BACKFILL_DEFAULT_DAYS) {
  return fetchApi<{ success: boolean; workflowInstanceId: string }>(
    '/marketing/sync/ga4/backfill',
    {
      method: 'POST',
      body: JSON.stringify({ days }),
    }
  );
}

/** One week of Search Console Sync now (`range` null: the default range); see `syncInWeeks`. */
export async function triggerSearchConsoleSync(range: SyncRange | null) {
  return fetchApi<WeeklySyncData>('/marketing/sync/search-console', {
    method: 'POST',
    body: JSON.stringify({ oneWeek: true, ...range }),
  });
}

export async function triggerSearchConsoleBackfill(days: number = MARKETING_BACKFILL_DEFAULT_DAYS) {
  return fetchApi<{ success: boolean; workflowInstanceId: string }>(
    '/marketing/sync/search-console/backfill',
    {
      method: 'POST',
      body: JSON.stringify({ days }),
    }
  );
}

export async function triggerMicrosoftAdsSync(startDate?: string, endDate?: string) {
  return fetchApi<{ success: boolean; rowsUpserted: number }>('/marketing/sync/microsoft-ads', {
    method: 'POST',
    body: JSON.stringify({ startDate, endDate }),
  });
}

export async function triggerMicrosoftAdsBackfill(days: number = MARKETING_BACKFILL_DEFAULT_DAYS) {
  return fetchApi<{ success: boolean; workflowInstanceId: string }>(
    '/marketing/sync/microsoft-ads/backfill',
    {
      method: 'POST',
      body: JSON.stringify({ days }),
    }
  );
}

export async function triggerMetaAdsSync(startDate?: string, endDate?: string) {
  return fetchApi<{ success: boolean; rowsUpserted: number }>('/marketing/sync/meta-ads', {
    method: 'POST',
    body: JSON.stringify({ startDate, endDate }),
  });
}

export async function triggerMetaAdsBackfill(days: number = MARKETING_BACKFILL_DEFAULT_DAYS) {
  return fetchApi<{ success: boolean; workflowInstanceId: string }>(
    '/marketing/sync/meta-ads/backfill',
    {
      method: 'POST',
      body: JSON.stringify({ days }),
    }
  );
}

export async function testInstagramIntegration() {
  return fetchApi<{
    status: string;
    account?: {
      name: string | null;
      igUserId?: string | null;
      username?: string | null;
      facebookPageId?: string | null;
      needsInstagramAccountSelection?: boolean;
      accountsAccessible?: number;
      pageCount?: number;
      grantedScopes?: string[];
      discoveryWarning?: string | null;
    };
    message?: string;
    details?: unknown;
  }>('/integrations/test-instagram', {});
}

export async function getInstagramAccounts() {
  return fetchApi<{
    accounts: {
      igUserId: string;
      username: string;
      pageId: string;
      pageName: string;
      followersCount: number | null;
    }[];
    pageCount?: number;
    pagesWithoutInstagram?: { pageId: string; pageName: string }[];
    grantedScopes?: string[];
    warning?: string | null;
  }>('/integrations/instagram/accounts', {});
}

export async function saveInstagramAccount(payload: {
  igUserId: string;
  username?: string;
  pageId?: string;
}) {
  return fetchApi<{ success: boolean }>('/integrations/instagram/account', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function triggerInstagramSync(startDate?: string, endDate?: string) {
  return fetchApi<{ success: boolean; rowsUpserted: number }>('/marketing/sync/instagram', {
    method: 'POST',
    body: JSON.stringify({ startDate, endDate }),
  });
}

export async function triggerInstagramBackfill(days: number = INSTAGRAM_BACKFILL_DEFAULT_DAYS) {
  return fetchApi<{ success: boolean; workflowInstanceId: string }>(
    '/marketing/sync/instagram/backfill',
    {
      method: 'POST',
      body: JSON.stringify({ days }),
    }
  );
}

export async function triggerShopifyMarketingSync() {
  return fetchApi<{
    success: boolean;
    rowsUpserted?: number;
    emailRowsUpserted?: number;
    searchRowsUpserted?: number;
    skipped?: boolean;
    reason?: string;
  }>(
    '/marketing/sync/shopify-marketing',
    { method: 'POST', body: JSON.stringify({}) }
  );
}

export async function triggerShopifyMarketingBackfill() {
  return fetchApi<{ success: boolean; workflowInstanceId: string }>(
    '/marketing/sync/shopify-marketing/backfill',
    { method: 'POST', body: JSON.stringify({}) }
  );
}

/** @deprecated Use triggerShopifyMarketingSync */
export async function triggerShopifyEmailSync() {
  return triggerShopifyMarketingSync();
}

/** @deprecated Use triggerShopifyMarketingBackfill */
export async function triggerShopifyEmailBackfill() {
  return triggerShopifyMarketingBackfill();
}

export type InstagramOverviewData = {
  connected: boolean;
  username: string | null;
  igUserId: string | null;
  summary: {
    followerCount: number | null;
    followersGained: number;
    followersLost: number;
    netFollowers: number;
    accountsEngaged: number;
    reach: number;
    impressions: number;
    totalInteractions: number;
    bioLinkClicks: number;
  };
  daily: Array<{
    date: string;
    followerCount: number;
    followersGained: number;
    followersLost: number;
    accountsEngaged: number;
    reach: number;
    impressions: number;
    totalInteractions: number;
  }>;
};

export async function getInstagramOverview(
  startDate: string,
  endDate: string,
  options: { signal?: AbortSignal } = {}
) {
  const q = new URLSearchParams({ startDate, endDate });
  return fetchApi<InstagramOverviewData>(`/marketing/instagram/overview?${q.toString()}`, {
    signal: options.signal,
  });
}

export type InstagramPostRow = {
  mediaId: string;
  mediaType: string | null;
  permalink: string | null;
  captionPreview: string | null;
  publishedAt: string | null;
  impressions: number;
  reach: number;
  likes: number;
  comments: number;
  saves: number;
  shares: number;
  engagement: number;
  bioLinkClicks: number;
  /** True when this post has ever been the creative of a Meta ad or a jflo post ad. */
  hasRunAsAd?: boolean;
  /** Campaign of an ad running on this post now (Meta ad active, or a jflo post ad not ended). */
  liveAdCampaign?: string | null;
  /** Instagram CDN still saved by the sync; null until stored (and the link can expire). */
  thumbnailUrl?: string | null;
};

export type InstagramPostsData = {
  connected: boolean;
  username: string | null;
  posts: InstagramPostRow[];
};

export async function getInstagramPosts(
  startDate: string,
  endDate: string,
  options: { signal?: AbortSignal } = {}
) {
  const q = new URLSearchParams({ startDate, endDate });
  return fetchApi<InstagramPostsData>(`/marketing/instagram/posts?${q.toString()}`, {
    signal: options.signal,
  });
}

export async function testFacebookIntegration() {
  return fetchApi<{
    status: string;
    account?: {
      name: string | null;
      pageId?: string | null;
      pageName?: string | null;
      needsPageSelection?: boolean;
      pagesAccessible?: number;
      grantedScopes?: string[];
      missingScopes?: string[];
      discoveryWarning?: string | null;
      reconnectGuidance?: string;
    };
    message?: string;
    reconnectGuidance?: string;
    details?: unknown;
  }>('/integrations/test-facebook', {});
}

export type FacebookPageOption = {
  pageId: string;
  pageName: string;
  followersCount: number | null;
  fansCount: number | null;
  eligible: boolean;
  eligibilityVia?: string | null;
  eligibilityMessage?: string | null;
};

export async function getFacebookPages() {
  return fetchApi<{
    pages: FacebookPageOption[];
    pageCount?: number;
    warning?: string | null;
  }>('/integrations/facebook/pages', {});
}

export async function saveFacebookPage(payload: { pageId: string; pageName?: string }) {
  return fetchApi<{ success: boolean }>('/integrations/facebook/page', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function triggerFacebookSync() {
  return fetchApi<{ success: boolean; rowsUpserted: number; warnings?: string[] }>(
    '/marketing/sync/facebook',
    { method: 'POST', body: JSON.stringify({}) }
  );
}

export async function triggerFacebookBackfill(days: number = FACEBOOK_BACKFILL_DEFAULT_DAYS) {
  return fetchApi<{ success: boolean; workflowInstanceId: string }>(
    '/marketing/sync/facebook/backfill',
    {
      method: 'POST',
      body: JSON.stringify({ days }),
    }
  );
}

export type FacebookOverviewDaily = {
  date: string;
  followersCount: number | null;
  followersGained: number | null;
  followersLost: number | null;
  mediaViewsTotal: number | null;
  mediaViewsNotFromAds: number | null;
  mediaViewsFromAds: number | null;
  mediaViewsFromNonFollowers: number | null;
  /** Unique viewers for that day only — never sum across days. */
  mediaViewersUniqueDaily: number | null;
  postEngagementsTotal: number | null;
  profileViewsTotal: number | null;
  actionsTotal: number | null;
};

export type FacebookOverviewData = {
  connected: boolean;
  pageId: string | null;
  pageName: string | null;
  summary: {
    followersCount: number | null;
    followersGained: number;
    followersLost: number;
    netFollowers: number;
    mediaViewsTotal: number;
    /** Organic views (`is_from_ads = false`). Null when breakdown never synced. */
    mediaViewsNotFromAds: number | null;
    mediaViewsFromAds: number | null;
    mediaViewsFromNonFollowers: number | null;
    /** Average of daily unique viewers — not period reach. */
    avgDailyUniqueViewers: number | null;
    postEngagementsTotal: number;
    profileViewsTotal: number;
    actionsTotal: number;
    organicViewShare: number | null;
    nonFollowerViewShare: number | null;
    engagementsPerView: number | null;
  };
  daily: FacebookOverviewDaily[];
};

export async function getFacebookOverview(
  startDate: string,
  endDate: string,
  options: { signal?: AbortSignal } = {}
) {
  const q = new URLSearchParams({ startDate, endDate });
  return fetchApi<FacebookOverviewData>(`/marketing/facebook/overview?${q.toString()}`, {
    signal: options.signal,
  });
}

export type FacebookPostRow = {
  postId: string;
  contentType: string;
  messagePreview: string | null;
  permalink: string | null;
  publishedAt: string;
  snapshotDate: string | null;
  mediaViewsTotal: number | null;
  mediaViewsFromAds: number | null;
  mediaViewsNotFromAds: number | null;
  mediaViewsFromNonFollowers: number | null;
  mediaViewersUniqueTotal: number | null;
  reactionsTotal: number | null;
  commentsTotal: number | null;
  sharesTotal: number | null;
  clicksTotal: number | null;
  boosted: boolean;
  organicViewShare: number | null;
  nonFollowerViewShare: number | null;
  engagementsPerView: number | null;
  clicksPerView: number | null;
  sharesPerView: number | null;
  commentsPerView: number | null;
  videoAvgTimeWatchedMs: number | null;
  videoCompleteViews30sOrganic: number | null;
  /** Delta vs prior snapshot when two snapshots exist; null if only one. */
  velocityViews: number | null;
};

export type FacebookPostsData = {
  connected: boolean;
  pageName: string | null;
  coverageNote: string;
  posts: FacebookPostRow[];
};

export async function getFacebookPosts(
  startDate: string,
  endDate: string,
  options: { signal?: AbortSignal } = {}
) {
  const q = new URLSearchParams({ startDate, endDate });
  return fetchApi<FacebookPostsData>(`/marketing/facebook/posts?${q.toString()}`, {
    signal: options.signal,
  });
}

export type FacebookContentInsightsData = {
  connected: boolean;
  byContentType: Array<{
    contentType: string;
    postCount: number;
    mediaViewsNotFromAds: number;
    engagements: number;
    clicks: number;
    avgEngagementsPerView: number | null;
  }>;
  byWeekday: Array<{
    weekday: number;
    label: string;
    postCount: number;
    mediaViewsNotFromAds: number;
    engagements: number;
  }>;
  byHour: Array<{
    hour: number;
    postCount: number;
    mediaViewsNotFromAds: number;
    engagements: number;
  }>;
};

export async function getFacebookContentInsights(
  startDate: string,
  endDate: string,
  options: { signal?: AbortSignal } = {}
) {
  const q = new URLSearchParams({ startDate, endDate });
  return fetchApi<FacebookContentInsightsData>(
    `/marketing/facebook/content-insights?${q.toString()}`,
    { signal: options.signal }
  );
}

export async function testAhrefsIntegration() {
  return fetchApi<{
    status: string;
    account?: {
      domain: string;
      hasApiKey: boolean;
    };
    message?: string;
    details?: unknown;
  }>('/integrations/test-ahrefs', {});
}

export async function saveAhrefsConnection(payload: {
  domain: string;
  apiKey?: string | null;
  clearApiKey?: boolean;
}) {
  return fetchApi<{ success: boolean; domain: string; hasApiKey: boolean }>(
    '/integrations/ahrefs/connection',
    {
      method: 'POST',
      body: JSON.stringify(payload),
    }
  );
}

export async function triggerAhrefsSync() {
  return fetchApi<{ success: boolean; rowsUpserted: number }>('/marketing/sync/ahrefs', {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export async function triggerAhrefsBackfill(days: number = MARKETING_BACKFILL_DEFAULT_DAYS) {
  return fetchApi<{ success: boolean; rowsUpserted: number; startDate?: string; endDate?: string }>(
    '/marketing/sync/ahrefs/backfill',
    {
      method: 'POST',
      body: JSON.stringify({ days }),
    }
  );
}

export type AhrefsDomainRatingMonth = {
  month: string;
  avgDomainRating: number;
  dayCount: number;
};

export async function getAhrefsDomainRatings() {
  return fetchApi<{ months: AhrefsDomainRatingMonth[] }>('/marketing/ahrefs/domain-ratings');
}

export async function saveAhrefsDomainRatingMonths(
  months: Array<{ month: string; domainRating: number }>
) {
  return fetchApi<{ success: boolean; dayCount: number; monthCount: number }>(
    '/marketing/ahrefs/domain-ratings',
    {
      method: 'POST',
      body: JSON.stringify({ months }),
    }
  );
}

export async function testStoqIntegration() {
  return fetchApi<{
    status: string;
    message?: string;
    account?: { connected?: boolean; hasApiKey: boolean; status?: string };
  }>('/integrations/test-stoq', {});
}

export async function saveStoqConnection(payload: { apiKey?: string | null; clearApiKey?: boolean }) {
  return fetchApi<{ success: boolean; hasApiKey: boolean; status: string }>(
    '/integrations/stoq/connection',
    { method: 'POST', body: JSON.stringify(payload) }
  );
}

/** 202 with the run; 409 `JOB_RUNNING` (with the running job's ids) while one runs. */
export async function triggerStoqSync() {
  return fetchApi<{ success: boolean; jobId: string; workflowId: string; status: 'queued' }>('/integrations/stoq/sync', {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export async function cancelStoqSync() {
  return fetchApi<StoqSyncStatus>('/integrations/stoq/cancel', { method: 'POST', body: JSON.stringify({}) });
}

export type StoqUnmatchedReason =
  | 'missing_shopify_variant'
  | 'shopify_variant_deleted'
  | 'unlinked_xero_item';

export type StoqUnmatchedPending = {
  stoqSignupId: string;
  shopifyVariantNumericId: string;
  shopifyProductNumericId: string | null;
  quantity: number;
  sku: string | null;
  shopifyTitle: string | null;
  reason: StoqUnmatchedReason;
};

export type StoqSyncStatus = {
  connected: boolean;
  hasApiKey: boolean;
  status: string;
  lastSignupsSyncedAt: string | null;
  lastConversionsSyncedAt: string | null;
  pendingCount: number;
  unmatchedPendingCount: number;
  unmatchedPending?: StoqUnmatchedPending[];
  /** The latest sync run, or null before the first. */
  run: JobRunSummary | null;
};

export async function getStoqSyncStatus() {
  return fetchApi<StoqSyncStatus>('/integrations/stoq/sync-status');
}

export interface SeoOverviewData {
  searchConsoleSiteUrl: string | null;
  organicTraffic: {
    totalSessions: number;
    totalUsers: number;
    totalConversions: number;
    totalRevenue: number;
    avgBounceRate: number;
  };
  dailyOrganic: Array<{
    date: string;
    sessions: number;
    users: number;
    conversions: number;
  }>;
  topQueries: Array<{
    query: string;
    impressions: number;
    clicks: number;
    ctr: number;
    avgPosition: number;
  }>;
  topPages: Array<{
    page: string;
    impressions: number;
    clicks: number;
    ctr: number;
    avgPosition: number;
  }>;
  trafficBySource: Array<{
    source: string;
    medium: string;
    sessions: number;
    users: number;
    conversions: number;
    revenue: number;
    bounceRate: number;
  }>;
}

export async function getSeoOverview(
  startDate: string,
  endDate: string,
  options: { signal?: AbortSignal } = {}
) {
  const q = new URLSearchParams({ startDate, endDate });
  return fetchApi<SeoOverviewData>(`/marketing/seo/overview?${q.toString()}`, {
    signal: options.signal,
  });
}

export interface MarketingPlatformMonthMetrics {
  spend: Array<number | null>;
  conversions: Array<number | null>;
  clicks: Array<number | null>;
  cpc: Array<number | null>;
  conversionValue: Array<number | null>;
  reach?: Array<number | null>;
  /** Meta Ads: spend on Instagram placements (publisher_platform breakdown). */
  instagramSpend?: Array<number | null>;
  /** Meta Ads: spend on Facebook placements. */
  facebookSpend?: Array<number | null>;
  /** Meta Ads: purchases on Instagram placements. */
  instagramPurchases?: Array<number | null>;
  /** Meta Ads: purchases on Facebook placements. */
  facebookPurchases?: Array<number | null>;
  /** Meta Ads: clicks on Instagram placements. */
  instagramClicks?: Array<number | null>;
  /** Meta Ads: clicks on Facebook placements. */
  facebookClicks?: Array<number | null>;
  /** Meta Ads: purchase value on Instagram placements. */
  instagramPurchaseValue?: Array<number | null>;
  /** Meta Ads: purchase value on Facebook placements. */
  facebookPurchaseValue?: Array<number | null>;
}

export interface MarketingSeoMonthMetrics {
  clicks: Array<number | null>;
  impressions: Array<number | null>;
  ctr: Array<number | null>;
  avgPosition: Array<number | null>;
  domainRating: Array<number | null>;
}

export interface MarketingInstagramMonthMetrics {
  followerCount: Array<number | null>;
  followersGained: Array<number | null>;
  followersLost: Array<number | null>;
  netFollowers: Array<number | null>;
  accountsEngaged: Array<number | null>;
  reach: Array<number | null>;
  impressions: Array<number | null>;
  totalInteractions: Array<number | null>;
  bioLinkClicks: Array<number | null>;
  engagement: Array<number | null>;
}

export interface MarketingFacebookOrganicMonthMetrics {
  followerCount: Array<number | null>;
  followersGained: Array<number | null>;
  followersLost: Array<number | null>;
  netFollowers: Array<number | null>;
  mediaViewsNotFromAds: Array<number | null>;
  avgDailyUniqueViewers: Array<number | null>;
  postEngagementsTotal: Array<number | null>;
  actionsTotal: Array<number | null>;
}

export interface MarketingGaTrafficMonthMetrics {
  direct: Array<number | null>;
  organic: Array<number | null>;
  paidGoogle: Array<number | null>;
  paidMeta: Array<number | null>;
  paidBing: Array<number | null>;
  other: Array<number | null>;
  total: Array<number | null>;
}

export interface MarketingStorefrontMonthMetrics {
  revenueExVat: number[];
  revenueOnline: number[];
  revenuePos: number[];
  /** GA4 sessions: null before GA4 data starts. */
  sessions: Array<number | null>;
  orders: number[];
  ordersOnline: number[];
  ordersPos: number[];
  averageBasket: Array<number | null>;
  conversionRate: Array<number | null>;
}

/** Single-month snapshot for MoM / YoY comparison columns. */
export interface MarketingMatrixMonthSnapshot {
  storefront: {
    revenueExVat: number;
    revenueOnline: number;
    revenuePos: number;
    sessions: number | null;
    orders: number;
    ordersOnline: number;
    ordersPos: number;
    averageBasket: number | null;
    conversionRate: number | null;
  };
  googleAds: {
    spend: number | null;
    conversions: number | null;
    clicks: number | null;
    cpc: number | null;
    conversionValue: number | null;
  };
  metaAds: {
    spend: number | null;
    conversions: number | null;
    clicks: number | null;
    cpc: number | null;
    conversionValue: number | null;
    reach: number | null;
    instagramSpend?: number | null;
    facebookSpend?: number | null;
    instagramPurchases?: number | null;
    facebookPurchases?: number | null;
    instagramClicks?: number | null;
    facebookClicks?: number | null;
    instagramPurchaseValue?: number | null;
    facebookPurchaseValue?: number | null;
  };
  bingAds: {
    spend: number | null;
    conversions: number | null;
    clicks: number | null;
    cpc: number | null;
    conversionValue: number | null;
  };
  seo: {
    clicks: number | null;
    impressions: number | null;
    ctr: number | null;
    avgPosition: number | null;
    domainRating: number | null;
  };
  instagram?: {
    followerCount: number | null;
    followersGained: number | null;
    followersLost: number | null;
    netFollowers: number | null;
    accountsEngaged: number | null;
    reach: number | null;
    impressions: number | null;
    totalInteractions: number | null;
    bioLinkClicks: number | null;
    engagement: number | null;
  };
  facebookOrganic?: {
    followerCount: number | null;
    followersGained: number | null;
    followersLost: number | null;
    netFollowers: number | null;
    mediaViewsNotFromAds: number | null;
    avgDailyUniqueViewers: number | null;
    postEngagementsTotal: number | null;
    actionsTotal: number | null;
  };
  shopifyEmail?: {
    campaignsSent: number;
    emailsSent: number;
    subscribersEstimated: number | null;
    openRate: number | null;
    clickRate: number | null;
    conversionRate: number | null;
    attributedOrders: number;
    attributedRevenue: number;
  };
  gaTraffic: {
    direct: number | null;
    organic: number | null;
    paidGoogle: number | null;
    paidMeta: number | null;
    paidBing: number | null;
    other: number | null;
    total: number | null;
  };
  totalPerformanceSpend: number | null;
}

export interface MarketingShopifyEmailMonthMetrics {
  campaignsSent: number[];
  emailsSent: number[];
  subscribersEstimated: Array<number | null>;
  openRate: Array<number | null>;
  clickRate: Array<number | null>;
  conversionRate: Array<number | null>;
  attributedOrders: number[];
  attributedRevenue: number[];
}

/** The matrix's data sources (DATA_SOURCES ids), for `dataStarts`. */
export type MarketingMatrixSource =
  | 'google_ads'
  | 'meta_ads'
  | 'microsoft_ads'
  | 'ga4'
  | 'search_console'
  | 'ahrefs'
  | 'instagram'
  | 'facebook';

/** `dataStarts` keys: each source, plus Instagram's profile and post metrics, which start apart. */
export type MarketingMatrixStartKey = MarketingMatrixSource | 'instagram_profile' | 'instagram_posts';

export interface MarketingPerformanceMatrixData {
  months: string[];
  currency: string;
  storefront: MarketingStorefrontMonthMetrics;
  googleAds: MarketingPlatformMonthMetrics;
  metaAds: MarketingPlatformMonthMetrics;
  bingAds: MarketingPlatformMonthMetrics;
  seo: MarketingSeoMonthMetrics;
  instagram?: MarketingInstagramMonthMetrics;
  facebookOrganic?: MarketingFacebookOrganicMonthMetrics;
  shopifyEmail?: MarketingShopifyEmailMonthMetrics | null;
  gaTraffic: MarketingGaTrafficMonthMetrics;
  /** Google + Meta + Microsoft spend; null for a month none of them has data for. */
  totalPerformanceSpend: Array<number | null>;
  /**
   * The first day each source has data for (YYYY-MM-DD), or null when it has none. Months before
   * it are null in that source's metrics, not 0 (decision 37 a).
   */
  dataStarts?: Partial<Record<MarketingMatrixStartKey, string | null>>;
  /** When the server worked this out (it comes from the server cache): the page's "As of" time. */
  cached_at?: string;
  comparison?: {
    previousMonth: string | null;
    yearAgoMonth: string | null;
    previous: MarketingMatrixMonthSnapshot | null;
    yearAgo: MarketingMatrixMonthSnapshot | null;
    /** First N days of comparison months when the range ends mid-month; null = full month. */
    partialDays?: number | null;
  };
}

export async function getMarketingPerformanceMatrix(
  startDate: string,
  endDate: string,
  options: { signal?: AbortSignal } = {}
) {
  const q = new URLSearchParams({ startDate, endDate });
  return fetchApi<MarketingPerformanceMatrixData>(
    `/marketing/performance-matrix?${q.toString()}`,
    { signal: options.signal }
  );
}

export interface GoogleAdsCampaignsData {
  campaigns: Array<{
    campaignId: string;
    campaignName: string;
    campaignType: string;
    campaignStatus: string;
    spend: number;
    impressions: number;
    clicks: number;
    ctr: number;
    avgCpc: number;
    conversions: number;
    conversionValue: number;
    costPerConversion: number;
    roas: number;
    viewThroughConversions: number;
    impressionTopPct: number | null;
    impressionAbsTopPct: number | null;
    reach?: number | null;
    frequency?: number | null;
  }>;
  daily: Array<{
    date: string;
    spend: number;
    conversions: number;
    conversionValue: number;
    clicks: number;
  }>;
  byType: Array<{
    campaignType: string;
    spend: number;
    clicks: number;
    conversions: number;
    conversionValue: number;
    roas: number;
    campaignCount: number;
  }>;
  byBrand: Array<{
    brand: string;
    spend: number;
    clicks: number;
    conversions: number;
    conversionValue: number;
    roas: number;
    campaignCount: number;
  }>;
  /** Meta Ads only: spend/clicks split by publisher_platform. */
  byPublisherPlatform?: Array<{
    publisherPlatform: string;
    spend: number;
    clicks: number;
    impressions: number;
    conversions: number;
    conversionValue: number;
    roas: number;
  }>;
  currency: string;
  accountName: string;
  /** Google Ads customer id for deep links; null if not connected. */
  accountId: string | null;
}

export async function getGoogleAdsCampaigns(
  startDate: string,
  endDate: string,
  options: { signal?: AbortSignal } = {}
) {
  const q = new URLSearchParams({ startDate, endDate });
  return fetchApi<GoogleAdsCampaignsData>(`/marketing/google-ads/campaigns?${q.toString()}`, {
    signal: options.signal,
  });
}

export type GoogleAdsBrandCoverageStatus =
  | 'dedicated'
  | 'generic_only'
  | 'not_advertised'
  | 'feed_blocked';

export interface GoogleAdsBrandCoverageRow {
  brand: string;
  /** Null when the brand only exists in the Merchant Center feed. */
  vendorId: string | null;
  collectionId: string | null;
  collectionHandle: string | null;
  feedItems: number;
  eligibleFeedItems: number;
  campaignCount: number;
  campaignSpend: number;
  campaignClicks: number;
  campaignCartRevenue: number;
  campaignCartOrders: number;
  searchTermCount: number;
  searchTermSpend: number;
  searchTermClicks: number;
  /** Search term spend from campaigns that are not dedicated to this brand. */
  genericSearchTermSpend: number;
  /** Distinct Merchant Center items of this brand that served in Shopping or PMax in the period. */
  productItemsServed: number;
  /** Shopping and PMax product spend for this brand's items, all campaigns. */
  productSpend: number;
  productClicks: number;
  /** Product spend from campaigns that are not dedicated to this brand. */
  genericProductSpend: number;
  productCartRevenue: number;
  productCartOrders: number;
  genericProductCartRevenue: number;
  /** Dedicated campaign spend plus generic search term and generic product spend. */
  attributedSpend: number;
  /** Dedicated campaign cart revenue plus generic product cart revenue. */
  attributedCartRevenue: number;
  /** Dedicated campaign cart orders plus generic product cart orders. */
  attributedCartOrders: number;
  salesValue: number;
  salesMargin: number;
  salesQuantity: number;
  spendShareOfSales: number | null;
  status: GoogleAdsBrandCoverageStatus;
}

/** Sales after an ad click where Google did not say which item was bought. */
export interface GoogleAdsUnattributedSold {
  units: number;
  revenue: number;
  days: number;
}

export interface GoogleAdsBrandCoverageData {
  startDate: string;
  endDate: string;
  brands: GoogleAdsBrandCoverageRow[];
  /** Campaign brand labels (for example "Other") whose spend is not split by brand. */
  unassignedCampaignGroups: Array<{
    label: string;
    campaignCount: number;
    spend: number;
    clicks: number;
    cartRevenue: number;
    /** Share of this spend that product-level data attributes to a brand. */
    productSplitSpend: number;
  }>;
  feedSnapshotDate: string | null;
  searchTermsFrom: string | null;
  searchTermsTo: string | null;
  /** Days in the range with published Shopping/PMax product data, against days in the range. */
  productDaysCovered: number;
  daysInRange: number;
  unattributedSold: GoogleAdsUnattributedSold;
}

export async function getGoogleAdsBrandCoverage(startDate: string, endDate: string) {
  const q = new URLSearchParams({ startDate, endDate });
  return fetchApi<GoogleAdsBrandCoverageData>(`/marketing/google-ads/brand-coverage?${q.toString()}`);
}

export interface GoogleAdsBrandCoverageItem {
  itemId: string;
  title: string | null;
  status: string | null;
  availability: string | null;
  productTypeL1: string | null;
  issueCodes: string[];
  /** jflo product id for linking; null when the feed item is not mapped. */
  productId: string | null;
  shopifyVariantId: string | null;
  sku: string | null;
  impressions: number;
  clicks: number;
  spend: number;
  cartRevenue: number;
  cartOrders: number;
  campaignCount: number;
  served: boolean;
}

export type GoogleAdsItemsFilter = { kind: 'brand' | 'product_type' | 'issue'; value: string };

export interface GoogleAdsBrandCoverageItemsData {
  filter: GoogleAdsItemsFilter;
  startDate: string;
  endDate: string;
  feedSnapshotDate: string | null;
  items: GoogleAdsBrandCoverageItem[];
  unattributedSold: GoogleAdsUnattributedSold;
}

export async function getGoogleAdsBrandCoverageItems(
  filter: GoogleAdsItemsFilter,
  startDate: string,
  endDate: string
) {
  const q = new URLSearchParams({ startDate, endDate });
  q.set(filter.kind === 'product_type' ? 'productType' : filter.kind, filter.value);
  return fetchApi<GoogleAdsBrandCoverageItemsData>(`/marketing/google-ads/brand-coverage/items?${q.toString()}`);
}

export interface GoogleAdsFeedHealthCounts {
  items: number;
  eligible: number;
  limited: number;
  notEligible: number;
}

export interface GoogleAdsFeedHealthData {
  feedSnapshotDate: string | null;
  channel: string;
  totals: GoogleAdsFeedHealthCounts;
  otherChannels: Array<{ channel: string } & GoogleAdsFeedHealthCounts>;
  byBrand: Array<
    GoogleAdsFeedHealthCounts & {
      brand: string;
      vendorId: string | null;
      topIssueCode: string | null;
      topIssueItems: number;
    }
  >;
  byProductType: Array<
    GoogleAdsFeedHealthCounts & {
      productType: string;
      topIssueCode: string | null;
      topIssueItems: number;
    }
  >;
  issues: Array<{
    errorCode: string;
    description: string | null;
    severity: string | null;
    detail: string | null;
    items: number;
    brands: number;
  }>;
}

export async function getGoogleAdsFeedHealth() {
  return fetchApi<GoogleAdsFeedHealthData>('/marketing/google-ads/feed-health');
}

export type PmaxAssetKindId =
  | 'headline'
  | 'long_headline'
  | 'description'
  | 'business_name'
  | 'image'
  | 'logo'
  | 'video';

export type PmaxAssetCounts = Record<PmaxAssetKindId, number>;

export interface PmaxAssetKind {
  id: PmaxAssetKindId;
  label: string;
  fieldTypes: string[];
  min: number;
  max: number;
  campaignLevel?: boolean;
}

export interface PmaxMetrics {
  impressions: number;
  clicks: number;
  cost: number;
  conversions: number;
  value: number;
}

export interface PmaxChannelCost {
  channel: string;
  cost: number;
  value: number;
}

export interface PmaxFlag {
  code: string;
  tone: 'critical' | 'warning' | 'info';
  label: string;
}

export interface PmaxPolicySummary {
  assets: number;
  eligible: number;
  limited: number;
  disapproved: number;
  notServing: number;
  pending: number;
  topics: Array<{ topic: string; assets: number }>;
}

export interface PmaxProductFilter {
  summary: string;
  dimension: string | null;
  included: string[];
  excluded: string[];
  everythingElse: 'included' | 'excluded' | null;
  nested: boolean;
}

export interface PmaxAssetGroupRow {
  campaignId: string;
  campaignName: string;
  campaignStatus: string | null;
  assetGroupId: string;
  name: string;
  status: string | null;
  primaryStatus: string | null;
  primaryStatusReasons: string[];
  adStrength: string | null;
  finalUrls: string[];
  productFilter: PmaxProductFilter;
  products: { included: number; servable: number; unknown: number } | null;
  counts: PmaxAssetCounts;
  generated: PmaxAssetCounts;
  countsFromCampaign: PmaxAssetKindId[];
  policy: PmaxPolicySummary;
  searchThemes: number;
  audienceSignals: number;
  metrics: PmaxMetrics;
  channels: PmaxChannelCost[];
  flags: PmaxFlag[];
}

export interface PmaxCampaignRow {
  campaignId: string;
  name: string;
  status: string | null;
  primaryStatus: string | null;
  primaryStatusReasons: string[];
  biddingStrategyType: string | null;
  targetCpa: number | null;
  targetRoas: number | null;
  dailyBudget: number | null;
  /** All its asset groups' results in the period, removed groups included. */
  metrics: PmaxMetrics;
}

export interface PmaxSyncState {
  jobId: string;
  status: string;
  running: boolean;
  setupOnly: boolean;
  startedAt: string | null;
  completedAt: string | null;
  error: string | null;
  datasets?: Array<{ dataset: string; status: string }>;
}

export interface PmaxOverviewData {
  sourceAccountId: string;
  currency: string | null;
  /** Google Ads' internal account id (`ocid`) for links into Google Ads; null until an admin saves it. */
  googleAdsOcid: string | null;
  startDate: string;
  endDate: string;
  observed: {
    assetGroups: string | null;
    assetLinks: string | null;
    listingGroups: string | null;
    merchantProducts: string | null;
    signals: string | null;
    fetchedAt: string | null;
  };
  resultsThrough: string | null;
  /** First day with channel results in the period (channel data starts later than group results). */
  channelsFrom: string | null;
  kinds: PmaxAssetKind[];
  campaigns: PmaxCampaignRow[];
  assetGroups: PmaxAssetGroupRow[];
  /** Every asset group's results in the period, removed groups included. */
  totals: PmaxMetrics;
  sync: PmaxSyncState | null;
}

/** One stored asset link with its content, as read by the asset group context. */
export interface PmaxAssetLink {
  link_level: 'asset_group' | 'campaign' | 'customer';
  campaign_id: string;
  asset_group_id: string;
  asset_id: string;
  field_type: string;
  status: string | null;
  primary_status: string | null;
  primary_status_reasons: string[] | null;
  source: string | null;
  approval_status: string | null;
  review_status: string | null;
  policy_topic_entries: Array<{ topic?: string; type?: string }> | null;
  content: {
    asset_type: string | null;
    name: string | null;
    text: string | null;
    image_url: string | null;
    image_width: number | null;
    image_height: number | null;
    youtube_video_id: string | null;
    youtube_video_title: string | null;
  } | null;
}

export interface PmaxSignal {
  setting_type: string;
  object_id: string;
  status: string | null;
  value: Record<string, unknown>;
}

/** An asset change made from jflo to a PMax asset group. */
export interface PmaxAssetChange {
  id: string;
  assetId: string | null;
  fieldType: string;
  action: 'add' | 'pause' | 'enable' | 'remove';
  text: string | null;
  imageUrl: string | null;
  status: 'pending' | 'applied' | 'validated' | 'failed' | 'unknown';
  error: string | null;
  createdAt: string;
  /** When Google answered; the page keeps showing the change until a sync after this. */
  completedAt: string | null;
  requestedBy: string | null;
}

export interface PmaxAssetGroupDetailData {
  startDate: string;
  endDate: string;
  metrics: PmaxMetrics;
  channels: PmaxChannelCost[];
  channelsFrom: string | null;
  assetMetrics: Array<PmaxMetrics & { assetId: string; fieldType: string }>;
  /** When Google's asset links were last fetched; jflo's changes since then aren't in `context` yet. */
  linksFetchedAt: string | null;
  /** When Google's asset group signals (search themes) were last fetched. */
  signalsFetchedAt: string | null;
  recentChanges: PmaxAssetChange[];
  /** The first day each asset was seen in this group (first impressions or first observation). */
  assetFirstSeen: Array<{ assetId: string; fieldType: string; firstSeen: string }>;
  /** jflo's asset history starts here: an asset first seen then may be older. */
  assetHistoryFrom: string | null;
  context: {
    assetGroupId: string;
    campaignId: string | null;
    observedDates: Record<string, string | null>;
    assetGroup: {
      name: string;
      status: string | null;
      ad_strength: string | null;
      final_urls: string[];
    } | null;
    productSelection: {
      included: Array<{
        item_id: string;
        title: string | null;
        brand: string | null;
        availability: string | null;
        status: string | null;
      }>;
      unknownCount: number;
      excludedCount: number;
      note: string | null;
    };
    assets: PmaxAssetLink[];
    signals: PmaxSignal[];
  };
}

export async function getGoogleAdsPmax(startDate: string, endDate: string) {
  const q = new URLSearchParams({ startDate, endDate });
  return fetchApi<PmaxOverviewData>(`/marketing/google-ads/pmax?${q}`);
}

export async function getGoogleAdsPmaxAssetGroup(
  assetGroupId: string,
  startDate: string,
  endDate: string
) {
  const q = new URLSearchParams({ startDate, endDate });
  return fetchApi<PmaxAssetGroupDetailData>(
    `/marketing/google-ads/pmax/asset-groups/${encodeURIComponent(assetGroupId)}?${q}`
  );
}

/** Refresh the PMax setup from Google now; answers with the job (an already running one if any). */
export async function startGoogleAdsPmaxSync() {
  return fetchApi<{ sync: PmaxSyncState; started: boolean }>(
    '/marketing/google-ads/pmax/sync',
    { method: 'POST', body: '{}' }
  );
}

/** Save the account's Google Ads `ocid` from any Google Ads address (or the bare number). */
export async function saveGoogleAdsPmaxLink(address: string) {
  return fetchApi<{ googleAdsOcid: string }>('/marketing/google-ads/pmax/google-ads-link', {
    method: 'PUT',
    body: JSON.stringify({ address }),
  });
}

export async function getGoogleAdsPmaxSync(jobId: string) {
  return fetchApi<PmaxSyncState>(
    `/marketing/google-ads/pmax/sync/${encodeURIComponent(jobId)}`
  );
}

export type GoogleAdsSearchTermClass = 'brand' | 'category' | 'generic';

export interface GoogleAdsSearchTermMetrics {
  terms: number;
  spend: number;
  clicks: number;
  impressions: number;
  conversions: number;
  conversionsValue: number;
}

export interface GoogleAdsSearchTermsReportData {
  startDate: string;
  endDate: string;
  termsFrom: string | null;
  termsTo: string | null;
  groups: Array<GoogleAdsSearchTermMetrics & { class: GoogleAdsSearchTermClass }>;
  /** One row per matched brand or category. */
  labels: Array<GoogleAdsSearchTermMetrics & { class: 'brand' | 'category'; label: string }>;
  /** Highest-spend terms that matched neither a brand nor a category. */
  unclassified: Array<GoogleAdsSearchTermMetrics & { term: string; campaigns: number }>;
}

export type GoogleAdsSearchTermRow = GoogleAdsSearchTermMetrics & { term: string; campaigns: number };

export interface GoogleAdsSearchTermsForLabelData {
  class: 'brand' | 'category';
  label: string;
  startDate: string;
  endDate: string;
  terms: GoogleAdsSearchTermRow[];
}

export async function getGoogleAdsSearchTermsForLabel(
  cls: 'brand' | 'category',
  label: string,
  startDate: string,
  endDate: string
) {
  const q = new URLSearchParams({ class: cls, label, startDate, endDate });
  return fetchApi<GoogleAdsSearchTermsForLabelData>(`/marketing/google-ads/search-terms/terms?${q.toString()}`);
}

export async function getGoogleAdsSearchTermsReport(startDate: string, endDate: string) {
  const q = new URLSearchParams({ startDate, endDate });
  return fetchApi<GoogleAdsSearchTermsReportData>(`/marketing/google-ads/search-terms?${q.toString()}`);
}

export interface GoogleAdsDatasetCoverageRow {
  dataset: string;
  kind: 'daily' | 'snapshot';
  firstDate: string | null;
  lastDate: string | null;
  expectedDays: number;
  publishedDays: number;
  failedDays: number;
  failedFrom: string | null;
  failedTo: string | null;
  pendingDays: number;
  untrackedDays: number;
  lastPublishedDate: string | null;
  lastPublishedAt: string | null;
  lastFailure: {
    date: string;
    at: string;
    errorClass: string | null;
    errorCode: string | null;
    message: string | null;
    recentCount: number;
  } | null;
  /** A Google Ads job covering this dataset that has not finished. */
  runningJob: { jobId: string; startedAt: string; dateFrom: string; dateTo: string } | null;
}

export interface GoogleAdsDatasetCoverageData {
  generatedAt: string;
  datasets: GoogleAdsDatasetCoverageRow[];
}

export type MarketingCoverageProvider =
  | 'google_ads'
  | 'ga4'
  | 'gsc'
  | 'meta_ads'
  | 'microsoft_ads'
  | 'onsite';

export async function getMarketingDatasetCoverage(provider: MarketingCoverageProvider) {
  return fetchApi<GoogleAdsDatasetCoverageData>(
    `/marketing/dataset-coverage?provider=${encodeURIComponent(provider)}`
  );
}

export async function retryGoogleAdsFailedDays(dataset: string) {
  return fetchApi<{ success: boolean; jobId: string; resumed: boolean; from: string; to: string; failedDays: number }>(
    '/marketing/google-ads/dataset-coverage/retry',
    { method: 'POST', body: JSON.stringify({ dataset }) }
  );
}

export async function getMicrosoftAdsCampaigns(
  startDate: string,
  endDate: string,
  options: { signal?: AbortSignal } = {}
) {
  const q = new URLSearchParams({ startDate, endDate });
  return fetchApi<GoogleAdsCampaignsData>(`/marketing/microsoft-ads/campaigns?${q.toString()}`, {
    signal: options.signal,
  });
}

export async function getMetaAdsCampaigns(
  startDate: string,
  endDate: string,
  options: { signal?: AbortSignal } = {}
) {
  const q = new URLSearchParams({ startDate, endDate });
  return fetchApi<GoogleAdsCampaignsData>(`/marketing/meta-ads/campaigns?${q.toString()}`, {
    signal: options.signal,
  });
}

/** Google Analytics, last click, matched on the UTM campaign tag. Null means no tagged sessions found. */
export interface MetaGaFigures {
  sessions: number;
  purchases: number;
  revenue: number;
}

/** Fetches first and last visit (UTM tags, referrer, landing page) of recent Shopify orders. Read-only. */
export async function syncShopifyOrderJourneys(days: number) {
  return fetchApi<
    | { status: 'synced'; orders: number; withCampaign: number; since: string }
    | { status: 'failed'; error: string }
  >('/marketing/sync/order-journeys', { method: 'POST', body: JSON.stringify({ days }) });
}

/** Shopify orders whose first or last visit carries a campaign's UTM tag. */
export interface MetaShopifyTotals {
  orders: number;
  /**
   * The advertised product's lines when revenueIsProduct (single-product post ad), the
   * campaign's Brand lines when revenueIsBrand, else the whole basket.
   */
  revenue: number;
  revenueIsBrand: boolean;
  revenueIsProduct: boolean;
  /** Everything in those orders, whatever Brand. */
  basketRevenue: number;
  newCustomers: number;
  /** Basket revenue split by whether it was the customer's first order. */
  newCustomerRevenue: number;
  returningCustomerRevenue: number;
}

export interface MetaCampaignOrderVisit {
  occurredAt: string | null;
  source: string | null;
  medium: string | null;
  campaign: string | null;
  term: string | null;
  landingPage: string | null;
  referrerUrl: string | null;
}

export interface MetaCampaignOrder {
  orderRefid: string;
  orderName: string | null;
  createdAt: string;
  newCustomer: boolean | null;
  daysToConversion: number | null;
  matchedOn: 'first' | 'last' | 'both';
  /** post: matched on the Instagram media id because the campaign tag was missing or unfilled. */
  matchedBy: 'campaign' | 'post';
  basketRevenue: number;
  brandRevenue: number | null;
  /** The advertised product's lines, for a single-product post ad. */
  productRevenue: number | null;
  brands: string[];
  first: MetaCampaignOrderVisit;
  last: MetaCampaignOrderVisit;
}

export type GoogleAdsPlacement = 'own_campaign' | 'other' | 'excluded';

export interface GoogleFeedBrand {
  brandId: string;
  brandName: string;
  placement: GoogleAdsPlacement | null;
  /** Variants of active products: the ones in the feed. */
  variants: number;
  /** Products or variants set differently from the Brand. */
  overrides: number;
  /** Variants that should carry a label (the Brand or an override decides one). */
  toLabel: number;
  /** Variants whose last written label matches the placement. */
  written: number;
  /** Variants whose label in Merchant Center matches. */
  inGoogle: number;
  listedInGoogle: number;
  /** Live variants Merchant Center does not list in any channel. */
  notInGoogle: number;
  /** Live variants Google will not advertise (policy, page or recall errors). */
  refusedByGoogle: number;
  /** Google's own product results for the Brand, last 60 days. */
  spend: number;
  revenue: number;
}

export interface GoogleFeedControlData {
  /** False until the placement migration is applied. */
  ready: boolean;
  /** Server switch; when false jflo refuses to write labels to Shopify. */
  writesOn: boolean;
  brands: GoogleFeedBrand[];
}

/** One Performance Max asset group's product filter, as Google Ads has it now. */
export interface GoogleAssetGroupFilter {
  campaignId: string;
  campaignName: string;
  assetGroupId: string;
  assetGroupName: string;
  /** brand, item_id, product_type, custom_label, category, condition, channel, other */
  splitsOn: string[];
  included: string[];
  excluded: string[];
  includedCount: number;
  excludedCount: number;
  everythingElse: 'included' | 'excluded' | null;
  /** True only when the asset group has no filter at all. */
  allProducts: boolean;
}

/** Per Brand: how many feed items some campaign advertises, and how many fall through. */
export interface GoogleBrandCoverage {
  brand: string;
  items: number;
  advertised: number;
  onlyByCatchAll: number;
  notAdvertised: number;
  unknown: number;
  campaigns: string[];
  notAdvertisedSample: string[];
}

export async function getGoogleFeedBrands() {
  return fetchApi<GoogleFeedControlData>('/marketing/google-feed/brands');
}

/** Live read of each PMax campaign's product filter, and the placement it implies per Brand. */
export async function getGoogleFeedCurrentFilters() {
  return fetchApi<{
    assetGroups: GoogleAssetGroupFilter[];
    coverage: GoogleBrandCoverage[];
    suggestions: Array<{ brandId: string; placement: GoogleAdsPlacement | null }>;
  }>('/marketing/google-feed/current-filters');
}

export interface GoogleFeedVariantRow {
  variantRefid: string;
  title: string | null;
  sku: string | null;
  override: GoogleAdsPlacement | null;
  effective: GoogleAdsPlacement | null;
  /** Google Ads product results, last 60 days, as Google reports them. */
  spend: number;
  revenue: number;
}

export interface GoogleFeedProductRow {
  productId: string;
  title: string;
  imageUrl: string | null;
  override: GoogleAdsPlacement | null;
  effective: GoogleAdsPlacement | null;
  spend: number;
  revenue: number;
  variants: GoogleFeedVariantRow[];
}

export interface GoogleBrandFeedProducts {
  brandId: string;
  brandName: string;
  brandPlacement: GoogleAdsPlacement | null;
  products: GoogleFeedProductRow[];
}

export async function getGoogleBrandFeedProducts(brandId: string) {
  return fetchApi<GoogleBrandFeedProducts>(
    `/marketing/google-feed/brands/${encodeURIComponent(brandId)}/products`
  );
}

export interface GoogleMissingFeedVariant {
  productId: string;
  productTitle: string;
  variantRefid: string;
  variantTitle: string | null;
  sku: string | null;
  price: number | null;
  inventory: number | null;
  imageUrl: string | null;
  /** Likely reasons the Google & YouTube app leaves it out. */
  hints: string[];
  /** On both Google & YouTube and Facebook & Instagram; null when jflo could not check. */
  onAdChannels: boolean | null;
}

export interface GoogleMissingFeedVariants {
  brandId: string;
  brandName: string;
  /** Newest Merchant Center snapshot date, yyyy-mm-dd. */
  lastSnapshot: string | null;
  /** False when jflo could not read the ad sales channels from Shopify. */
  channelKnown: boolean;
  variants: GoogleMissingFeedVariant[];
}

export interface GoogleRefusal {
  reason: string;
  detail: string | null;
  documentation: string | null;
}

export interface GoogleRefusedFeedVariant {
  productId: string;
  productTitle: string;
  variantRefid: string;
  variantTitle: string | null;
  sku: string | null;
  imageUrl: string | null;
  channels: string[];
  refusals: GoogleRefusal[];
}

export interface GoogleRefusedFeedVariants {
  brandId: string;
  brandName: string;
  lastSnapshot: string | null;
  variants: GoogleRefusedFeedVariant[];
}

export async function getGoogleRefusedFeedVariants(brandId: string) {
  return fetchApi<GoogleRefusedFeedVariants>(
    `/marketing/google-feed/brands/${encodeURIComponent(brandId)}/refused`
  );
}

/** Publishes products to Google & YouTube and Facebook & Instagram together (at most 40 at a time). */
export async function publishProductsToAdChannels(productIds: string[]) {
  return fetchApi<{ published: number; errors: string[] }>(`/marketing/google-feed/publish-ad-channels`, {
    method: 'POST',
    body: JSON.stringify({ productIds }),
  });
}

export async function getGoogleMissingFeedVariants(brandId: string) {
  return fetchApi<GoogleMissingFeedVariants>(
    `/marketing/google-feed/brands/${encodeURIComponent(brandId)}/missing`
  );
}

export interface GooglePlacementTarget {
  /** False when the product or variant is not in an active Shopify product. */
  found: boolean;
  brandName: string | null;
  brandPlacement: GoogleAdsPlacement | null;
  productOverride: GoogleAdsPlacement | null;
  variantOverride: GoogleAdsPlacement | null;
  effective: GoogleAdsPlacement | null;
  variants: number;
  written: number;
}

export async function getGooglePlacementTarget(kind: 'product' | 'variant', targetId: string) {
  const q = new URLSearchParams({ kind, targetId });
  return fetchApi<GooglePlacementTarget>(`/marketing/google-feed/target?${q.toString()}`);
}

export interface GooglePlacementOverrideChange {
  kind: 'product' | 'variant';
  /** The product id, or the Shopify variant id. */
  targetId: string;
  /** null removes the override. */
  placement: GoogleAdsPlacement | null;
}

/** Saves product and variant overrides in jflo only. */
export async function saveGooglePlacementOverrides(changes: GooglePlacementOverrideChange[]) {
  return fetchApi<{ saved: number }>('/marketing/google-feed/overrides', {
    method: 'PUT',
    body: JSON.stringify({ changes }),
  });
}

/** Saves placements in jflo only. */
export async function saveGoogleFeedPlacements(
  changes: Array<{ brandId: string; placement: GoogleAdsPlacement | null }>
) {
  return fetchApi<{ saved: number }>('/marketing/google-feed/placements', {
    method: 'PUT',
    body: JSON.stringify({ changes }),
  });
}

/** Writes one Brand's Google label to its Shopify variants. Call again while remaining > 0. */
export async function applyGoogleFeedLabels(brandId: string) {
  return fetchApi<{ brandId: string; written: number; remaining: number; errors: string[] }>(
    `/marketing/google-feed/brands/${encodeURIComponent(brandId)}/apply`,
    { method: 'POST' }
  );
}

export type AdPlatform = 'meta' | 'google';

export interface MetaCampaignOrdersQuery {
  platform: AdPlatform;
  campaignId: string | null;
  campaignName: string | null;
  brandName: string | null;
  /** The advertised product of a single-product post ad. */
  productId?: string | null;
  /** Instagram media id of a post ad. */
  mediaId?: string | null;
  /** Orders from the platform that belong to none of its campaigns. */
  unmatched?: boolean;
  startDate: string;
  endDate: string;
}

export async function getMetaCampaignOrders(args: MetaCampaignOrdersQuery) {
  const q = new URLSearchParams({
    platform: args.platform,
    startDate: args.startDate,
    endDate: args.endDate,
  });
  if (args.campaignId) q.set('campaignId', args.campaignId);
  if (args.campaignName) q.set('campaignName', args.campaignName);
  if (args.brandName) q.set('brandName', args.brandName);
  if (args.productId) q.set('productId', args.productId);
  if (args.mediaId) q.set('mediaId', args.mediaId);
  if (args.unmatched) q.set('unmatched', '1');
  return fetchApi<{ ready: boolean; totals: MetaShopifyTotals | null; orders: MetaCampaignOrder[] }>(
    `/marketing/campaign-orders?${q.toString()}`
  );
}

export interface CampaignRealOrdersRow {
  campaignId: string;
  campaignName: string;
  brandName: string | null;
  mediaId: string | null;
  spend: number;
  /** What the ad platform itself reports. */
  platformPurchases: number;
  platformRevenue: number;
  ga: MetaGaFigures | null;
  shopify: MetaShopifyTotals;
}

export interface CampaignRealOrdersData {
  /** False until Shopify order journeys have been fetched. */
  ready: boolean;
  campaigns: CampaignRealOrdersRow[];
  unmatched: MetaShopifyTotals | null;
}

export async function getCampaignRealOrders(platform: AdPlatform, startDate: string, endDate: string) {
  const q = new URLSearchParams({ platform, startDate, endDate });
  return fetchApi<CampaignRealOrdersData>(`/marketing/campaign-real-orders?${q.toString()}`);
}

export interface MetaAdSummary {
  adId: string;
  name: string;
  effectiveStatus: string | null;
  instagramMediaId: string | null;
  instagramPermalinkUrl: string | null;
  callToActionType: string | null;
  linkUrl: string | null;
  urlTags: string | null;
  productSetId: string | null;
  thumbnailUrl: string | null;
}

export interface MetaAdSetRow {
  adsetId: string;
  name: string;
  campaignId: string;
  campaignName: string | null;
  campaignObjective: string | null;
  campaignSpendCap: number | null;
  effectiveStatus: string | null;
  optimizationGoal: string | null;
  billingEvent: string | null;
  bidStrategy: string | null;
  destinationType: string | null;
  dailyBudget: number | null;
  lifetimeBudget: number | null;
  budgetRemaining: number | null;
  bidAmount: number | null;
  /** Over the last sync window (windowStart to windowEnd), not the page's date range. */
  windowReach: number | null;
  windowFrequency: number | null;
  windowStart: string | null;
  windowEnd: string | null;
  currency: string | null;
  startTime: string | null;
  endTime: string | null;
  targeting: Record<string, unknown> | null;
  promotedObject: Record<string, unknown> | null;
  ads: MetaAdSummary[];
  impressions: number;
  clicks: number;
  spend: number;
  purchases: number;
  purchaseValue: number;
  addToCarts: number;
  /** For the whole campaign over the same dates. */
  ga: MetaGaFigures | null;
  /** Null until Shopify order journeys have been fetched. */
  shopify: MetaShopifyTotals | null;
  brandName: string | null;
  syncedAt: string | null;
}

export interface MetaAdSetsData {
  /** False until the ad set tables exist and a Meta Ads sync has filled them. */
  ready: boolean;
  adsets: MetaAdSetRow[];
}

export async function getMetaAdSets(startDate: string, endDate: string) {
  const q = new URLSearchParams({ startDate, endDate });
  return fetchApi<MetaAdSetsData>(`/marketing/meta-ads/adsets?${q.toString()}`);
}

export interface MetaSuggestedPost {
  mediaId: string;
  mediaType: string | null;
  permalink: string | null;
  captionPreview: string | null;
  publishedAt: string | null;
  reach: number;
  likes: number;
  comments: number;
  saves: number;
  shares: number;
  engagementRate: number;
  /** Still saved by the Instagram sync; null until that sync has stored one. */
  thumbnailUrl: string | null;
  brandGuess: {
    brandId: string;
    brandName: string;
    matchedOn: 'words' | 'hashtag';
  } | null;
}

export interface MetaSuggestedPostsData {
  connected: boolean;
  username: string | null;
  maxAgeDays: number;
  posts: MetaSuggestedPost[];
  /** Every post that qualifies; more than posts.length means there are more to load. */
  total: number;
}

/** The best-engaging recent posts, five unless a larger count is asked for. */
export async function getMetaSuggestedPosts(count?: number) {
  const q = count ? `?count=${count}` : '';
  return fetchApi<MetaSuggestedPostsData>(`/marketing/meta-ads/suggested-posts${q}`);
}

/** Any one Instagram post, shaped like a suggested post, for Create ad outside the top five. */
export async function getMetaPostCandidate(mediaId: string) {
  return fetchApi<{ post: MetaSuggestedPost | null; problem: string | null }>(
    `/marketing/meta-ads/post-candidate/${encodeURIComponent(mediaId)}`
  );
}

export interface MetaCaptionFlag {
  key: string;
  label: string;
  advice: string;
  /** Jev's probability that the caption has this problem. */
  probability: number;
}

export interface MetaCaptionCheck {
  mediaId: string;
  caption: string | null;
  /** Null when the check could not run; see reason. */
  flags: MetaCaptionFlag[] | null;
  reason: string | null;
}

/** Jev reads the post's full caption for things that read wrong in an ad. */
export async function checkMetaPostCaption(mediaId: string) {
  return fetchApi<MetaCaptionCheck>(`/marketing/meta-ads/post-caption/${encodeURIComponent(mediaId)}`);
}

/** What Instagram says about using a post as an ad (read-only). */
export interface MetaPostAdEligibility {
  mediaId: string;
  mediaType: string | null;
  productType: string | null;
  eligibleToBoost: boolean | null;
  ineligibleReason: string | null;
  collaborators: string[] | null;
  children: Array<{ id: string; mediaType: string | null }> | null;
  /** Null when jflo may not read product tags (reconnect Meta Ads). */
  productTagCount: number | null;
  notes: string[];
}

export async function getMetaPostAdEligibility(mediaId: string) {
  return fetchApi<MetaPostAdEligibility>(
    `/marketing/meta-ads/post-ad-eligibility/${encodeURIComponent(mediaId)}`
  );
}

/** Re-reads the post from Instagram (after editing its caption there), then checks it again. */
export async function refreshMetaPostCaption(mediaId: string) {
  return fetchApi<MetaCaptionCheck>(
    `/marketing/meta-ads/post-caption/${encodeURIComponent(mediaId)}/refresh`,
    { method: 'POST' }
  );
}

/** Whether a product can be sold now (as the post ad stock rule counts it), and its units on hand. */
export interface MetaProductStock {
  inStock: boolean;
  quantity: number;
}

export interface MetaBrandProducts {
  activeCount: number;
  /** How many active products are in stock, as the stock rule counts it. */
  inStockCount: number;
  products: Array<{ id: string; title: string; imageUrl: string | null } & MetaProductStock>;
  /** Every active product of the Brand, for the stock rule and single-product post ads. */
  options: Array<
    { id: string; title: string; handle: string | null; imageUrl: string | null } & MetaProductStock
  >;
}

/** A few of a Brand's active products, to check a Brand guess by eye. */
export async function getMetaBrandProducts(brandId: string) {
  return fetchApi<MetaBrandProducts>(
    `/marketing/meta-ads/brand-products/${encodeURIComponent(brandId)}`
  );
}

export interface MetaAdRecipeEdits {
  name?: string;
  audience?: 'broad' | 'imported';
  ageMin?: number;
  ageMax?: number;
  countEngagedVideoViews?: boolean;
  defaultLifetimeBudget?: number | null;
  defaultRunDays?: number | null;
  defaultStockMinShare?: number | null;
  defaultStage2Budget?: number | null;
  defaultStage2Days?: number | null;
  defaultStage2MinRevenue?: number | null;
}

/** Saves edits to an ad recipe in jflo. Existing post ads are not changed. */
export async function updateMetaAdRecipe(recipeId: string, edits: MetaAdRecipeEdits) {
  return fetchApi<MetaAdRecipe>(`/marketing/meta-ads/recipes/${encodeURIComponent(recipeId)}`, {
    method: 'PUT',
    body: JSON.stringify(edits),
  });
}

export interface MetaAdRecipe {
  id: string;
  name: string;
  sourceCampaignId: string | null;
  /** The settings as the edit form shows them. */
  edit: {
    audience: 'broad' | 'imported';
    ageMin: number;
    ageMax: number;
    countEngagedVideoViews: boolean;
  };
  settings: Record<string, unknown>;
  importNotes: string[];
  createdAt: string | null;
  updatedAt: string | null;
}

export async function getMetaAdRecipes() {
  return fetchApi<{ ready: boolean; recipes: MetaAdRecipe[] }>(
    '/marketing/meta-ads/recipes'
  );
}

/** Saves a synced campaign's settings as an ad recipe in jflo. Does not call Meta. */
export async function importMetaAdRecipe(campaignId: string, name?: string) {
  return fetchApi<MetaAdRecipe>('/marketing/meta-ads/recipes/import', {
    method: 'POST',
    body: JSON.stringify({ campaignId, name }),
  });
}

/** Rebuilds a recipe from its source campaign as last synced. Does not call Meta. */
export async function reimportMetaAdRecipe(recipeId: string) {
  return fetchApi<MetaAdRecipe>(
    `/marketing/meta-ads/recipes/${encodeURIComponent(recipeId)}/reimport`,
    { method: 'POST' }
  );
}

export interface MetaPostAdRow {
  id: string;
  name: string;
  instagramMediaId: string;
  instagramPermalink: string | null;
  captionPreview: string | null;
  thumbnailUrl: string | null;
  brandName: string | null;
  /** Set for a single-product post ad (no catalog beside the post). */
  productId: string | null;
  productTitle: string | null;
  status: 'building' | 'active' | 'paused' | 'failed' | 'ended';
  step: string;
  errorMessage: string | null;
  lifetimeBudget: number;
  startTime: string | null;
  endTime: string | null;
  destinationUrl: string;
  metaAdsetId: string | null;
  metaCampaignId: string | null;
  /** The ad account (act_...), for Ads Manager links. */
  accountId: string | null;
  brandId: string | null;
  rules: MetaPostAdRuleView;
  spend: number;
  purchases: number;
  purchaseValue: number;
  addToCarts: number;
  /** From the start date to today. */
  ga: MetaGaFigures | null;
  /** Null until Shopify order journeys have been fetched. */
  shopify: MetaShopifyTotals | null;
  campaignId: string | null;
  campaignName: string | null;
}

export interface MetaPostAdLimits {
  perAdCap: number;
  monthlyCap: number;
}

export interface MetaPostAdsData {
  /** False until the post ad tables exist. */
  ready: boolean;
  limits: MetaPostAdLimits | null;
  month: {
    month: string;
    committed: number;
    spent: number;
  };
  storeOrigin: string | null;
  /** collectionHandle: the Brand's own published collection, for the ad's link; null when none. */
  brands: Array<{ id: string; name: string; collectionHandle: string | null }>;
  postAds: MetaPostAdRow[];
  writeAccess: {
    /** Server switch; when false jflo refuses every call that would change Meta. */
    writesOn: boolean;
    missingScopes: string[];
  };
}

export interface MetaPostAdInput {
  recipeId: string;
  instagramMediaId: string;
  instagramPermalink: string | null;
  captionPreview: string | null;
  brandId: string | null;
  brandName: string | null;
  /** A single-product post ad: no catalog; the Brand and stock rule follow this product. */
  productId?: string | null;
  lifetimeBudget: number;
  startTime: string;
  endTime: string;
  destinationUrl: string;
  callToActionType: string;
  /** Build everything but leave it paused, to check in Ads Manager before it spends. */
  leavePaused?: boolean;
  requiredProductIds?: string[];
  stockMinShare?: number | null;
  stage2Budget?: number | null;
  stage2Days?: number | null;
  stage2MinRevenue?: number | null;
}

export interface MetaPostAdPreview {
  problems: string[];
  committedThisMonth: number;
  limits: MetaPostAdLimits | null;
  calls: Array<{ path: string; params: Record<string, string> }>;
  /** The product the destination links to, for the stock rule's default. */
  destinationProduct: { id: string; title: string } | null;
  /** Also the utm_campaign tag in Analytics and on Shopify orders. */
  campaignName: string;
  /** The stock rule on today's stock: what it would do with this ad now. */
  stock: {
    verdict: { state: 'ok' | 'paused_required' | 'paused_share'; reason: string };
    advertised: number;
    advertisedInStock: number;
  };
}

/** A post ad's stock rule and two-stage budget; all editable, before and after creation. */
export interface MetaPostAdRules {
  /** Products that must each be in stock, else the ad pauses. */
  requiredProductIds: string[];
  /** Share (0 to 1) of advertised products that must be in stock; null = off. */
  stockMinShare: number | null;
  /** Added to the budget when stage 1 earns enough; null = one stage. */
  stage2Budget: number | null;
  stage2Days: number | null;
  /** Shopify-matched order revenue stage 1 must reach. */
  stage2MinRevenue: number | null;
}

export interface MetaPostAdRuleView extends MetaPostAdRules {
  stockRuleOff: boolean;
  /** ok | paused_required | paused_share */
  stockState: string;
  stockReason: string | null;
  stockCheckedAt: string | null;
  stockPausedAt: string | null;
  stage: number;
  /** extended | ended_below | ended_stock | ended_no_room | ended_failed */
  stageDecision: string | null;
  stageReason: string | null;
  stageDecidedAt: string | null;
}

/** Saves one post ad's rules in jflo. The daily run applies them. */
export async function updateMetaPostAdRules(id: string, rules: MetaPostAdRules & { stockRuleOff: boolean }) {
  return fetchApi<{ saved: true }>(`/marketing/meta-ads/post-ads/${encodeURIComponent(id)}/rules`, {
    method: 'PUT',
    body: JSON.stringify(rules),
  });
}

export interface MetaPostAdRulesResult {
  status: 'off' | 'done';
  checked: number;
  paused: string[];
  resumed: string[];
  extended: string[];
  ended: string[];
  errors: string[];
}

/** Runs the stock rule and stage decisions now. May pause, resume or extend ads in Meta. */
export async function checkMetaPostAdRules() {
  return fetchApi<MetaPostAdRulesResult>('/marketing/meta-ads/post-ads/check-rules', { method: 'POST' });
}

export async function getMetaPostAds() {
  return fetchApi<MetaPostAdsData>('/marketing/meta-ads/post-ads');
}

/** What creating this post ad would send to Meta. Calls nothing in Meta. */
export async function previewMetaPostAd(input: MetaPostAdInput) {
  return fetchApi<MetaPostAdPreview>('/marketing/meta-ads/post-ads/preview', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** Creates the post ad in Meta (spends money once it is active). */
export async function createMetaPostAd(input: MetaPostAdInput) {
  return fetchApi<{ id: string; status: string; error_message: string | null }>(
    '/marketing/meta-ads/post-ads',
    { method: 'POST', body: JSON.stringify(input) }
  );
}

export async function retryMetaPostAd(id: string) {
  return fetchApi<{
    id: string;
    status: string;
    step: string;
    error_message: string | null;
  }>(
    `/marketing/meta-ads/post-ads/${encodeURIComponent(id)}/retry`,
    { method: 'POST' }
  );
}

export async function setMetaPostAdPaused(id: string, paused: boolean) {
  return fetchApi<{ id: string; status: string }>(
    `/marketing/meta-ads/post-ads/${encodeURIComponent(id)}/${paused ? 'pause' : 'resume'}`,
    { method: 'POST' }
  );
}

/** Deletes a paused or failed post ad that never spent (in Meta too), freeing its post. */
export async function deleteMetaPostAd(id: string) {
  return fetchApi<{ deleted: boolean }>(
    `/marketing/meta-ads/post-ads/${encodeURIComponent(id)}/delete`,
    { method: 'POST' }
  );
}

export async function saveMetaPostAdLimits(limits: MetaPostAdLimits) {
  return fetchApi<{ limits: MetaPostAdLimits }>(
    '/marketing/meta-ads/post-ads/limits',
    { method: 'PUT', body: JSON.stringify(limits) }
  );
}

/** Versioned marketing reads live in `frontend/src/lib/marketing-v2-api.ts`. */

export interface MarketingBudget {
  platform: string;
  month: string;
  budgetAmount: number;
  currency: string;
}

export interface MarketingBudgetInput {
  platform: string;
  month: string;
  budgetAmount: number;
  currency?: string;
}

export interface MarketingSettings {
  roasFloor: number;
  minSpendForAlerts: number;
  pacingAlertThresholdPercent: number;
  /** Goal return on Google Ads for the Budgets page; 0 when not set. */
  googleAdsGoalRoas: number;
}

export interface CampaignBrandPreviewRow {
  platform: string;
  campaignId: string;
  campaignName: string;
  brand: string;
}

export interface MarketingPacingRow {
  platform: string;
  budgetAmount: number;
  spendToDate: number;
  daysElapsed: number;
  daysInMonth: number;
  projectedSpend: number;
  pacePercentage: number;
  status: 'on_track' | 'over_pacing' | 'under_pacing';
}

export type MarketingAlertSeverity = 'critical' | 'warning' | 'info';
/** The Ads check kind behind the alert (T-19). */
export type MarketingAlertType = GoogleAdsCheckKind;

export interface MarketingAlert {
  id: string;
  severity: MarketingAlertSeverity;
  type: MarketingAlertType;
  title: string;
  description: string;
  platform: string;
  campaignId?: string;
  campaignName?: string;
  /** The campaign in its platform. */
  actionUrl?: string;
  /** Where to fix it in jflo (a path). */
  href?: string;
}

export async function getMarketingBudgets(month: string) {
  const q = new URLSearchParams({ month });
  return fetchApi<{ budgets: MarketingBudget[] }>(`/marketing/budgets?${q.toString()}`);
}

export async function saveMarketingBudgets(budgets: MarketingBudgetInput[]) {
  return fetchApi<{ success: boolean }>('/marketing/budgets', {
    method: 'POST',
    body: JSON.stringify({ budgets }),
  });
}

export async function getMarketingSettings() {
  return fetchApi<{ settings: MarketingSettings; campaignBrandPreview: CampaignBrandPreviewRow[] }>(
    '/marketing/settings'
  );
}

export async function saveMarketingSettings(settings: MarketingSettings) {
  return fetchApi<{ success: boolean; settings: MarketingSettings }>('/marketing/settings', {
    method: 'POST',
    body: JSON.stringify(settings),
  });
}

export async function getMarketingAlerts() {
  return fetchApi<{ alerts: MarketingAlert[] }>('/marketing/alerts');
}

export async function getMarketingPacing(month: string) {
  const q = new URLSearchParams({ month });
  return fetchApi<{ pacing: MarketingPacingRow[] }>(`/marketing/pacing?${q.toString()}`);
}

// --- Shopify collections (hierarchy + drafts) ---

/** Same delimiter as worker `COLLECTION_PATH_SEP` for splitting `fullname`. */
export const COLLECTION_PATH_SEP = ' \u203a ';

export interface CollectionRow {
  id: string;
  refid: string;
  parent_id: string | null;
  sort_order: number;
  depth: number;
  in_hierarchy: boolean;
  is_smart: boolean;
  title: string;
  handle?: string | null;
  is_published?: boolean;
  /** Shopify publication GIDs (sales channels) this collection is on. */
  publication_ids?: string[];
  /** Staged set_publications / publish_collection not yet committed */
  is_pending_publications?: boolean;
  /** Linked products in app data (matches collection product list in direct mode). */
  products_count?: number;
  /** Root-to-leaf title path from the server or derived for pending creates */
  fullname?: string | null;
  /** Root-to-leaf id path */
  fulluuid?: string | null;
  /** Staged create not yet in Shopify / local snapshot */
  is_pending_create?: boolean;
  /** Staged delete_collection on the current draft (or in-flight commit) */
  is_pending_delete?: boolean;
  /** Staged rename_collection; title/handle reflect the draft until commit */
  is_pending_rename?: boolean;
  /** Staged convert_smart_to_manual; draggable in tree before commit */
  is_pending_convert?: boolean;
  /** Staged update_collection_content on the product-content draft */
  is_pending_content?: boolean;
  /** Include in Shopify Online Store main menu when pushing */
  menu_sync_enabled?: boolean;
  /** Shopify MenuItem GID for parent slot; null/undefined = top level */
  menu_parent_item_id?: string | null;
  /** Omit from main menu when a mapped ancestor would include this subtree */
  menu_sync_excluded?: boolean;
  /** Store-relative menu link override; null = default /collections/{handle} */
  menu_url_override?: string | null;
  /** Content filled in, staged content included; unset for staged creates. */
  has_description?: boolean;
  has_seo_title?: boolean;
  has_seo_description?: boolean;
}

export async function getCollectionsTree() {
  return fetchApi<{ collections: CollectionRow[] }>('/collections/tree');
}

export type CollectionBrandCheck = {
  /** Every Brand (Shopify's Vendor), with its active products. */
  brands: { name: string; active_products: number }[];
  /** For each collection under "Brands": the Brands its products are from, most products first. */
  brands_by_collection: Record<string, { name: string; products: number }[]>;
};

export async function getCollectionBrandCheck() {
  return fetchApi<CollectionBrandCheck>('/collections/brand-check');
}

export type CollectionsListQueryParams = {
  page?: number;
  pageSize?: number;
  search?: string;
  /** CSV or array: smart | manual */
  type?: Array<'smart' | 'manual'> | string;
  published?: boolean;
  inHierarchy?: boolean;
  seoTitle?: 'present' | 'missing';
  seoDescription?: 'present' | 'missing';
  description?: 'present' | 'missing';
  sortBy?: 'fullname' | 'title' | 'products_count' | 'updated_at';
  sortDir?: 'asc' | 'desc';
};

export type CollectionListItem = {
  id: string;
  refid: string;
  title: string;
  handle: string | null;
  fullname: string | null;
  depth: number;
  is_smart: boolean;
  is_published: boolean;
  in_hierarchy: boolean;
  products_count: number;
  seo_title: string | null;
  seo_description: string | null;
  description_html: string | null;
  updated_at: string;
};

export type CollectionsListResponse = {
  collections: CollectionListItem[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
};

export async function getCollectionsList(
  params: CollectionsListQueryParams = {},
  options: { signal?: AbortSignal } = {}
) {
  const qs = new URLSearchParams();
  if (params.page != null) qs.set('page', String(params.page));
  if (params.pageSize != null) qs.set('pageSize', String(params.pageSize));
  if (params.search) qs.set('search', params.search);
  if (params.type != null) {
    const typeVal = Array.isArray(params.type)
      ? params.type.join(',')
      : params.type;
    if (typeVal) qs.set('type', typeVal);
  }
  if (params.published != null) qs.set('published', String(params.published));
  if (params.inHierarchy != null) {
    qs.set('inHierarchy', String(params.inHierarchy));
  }
  if (params.seoTitle) qs.set('seoTitle', params.seoTitle);
  if (params.seoDescription) qs.set('seoDescription', params.seoDescription);
  if (params.description) qs.set('description', params.description);
  if (params.sortBy) qs.set('sortBy', params.sortBy);
  if (params.sortDir) qs.set('sortDir', params.sortDir);
  const query = qs.toString();
  return fetchApi<CollectionsListResponse>(
    `/collections/list${query ? `?${query}` : ''}`,
    { method: 'GET', signal: options.signal }
  );
}

export async function getCollectionProductCounts(statuses: readonly string[]) {
  const params = new URLSearchParams();
  if (statuses.length > 0) {
    params.set('statuses', statuses.join(','));
  }
  const query = params.toString();
  return fetchApi<{ counts: Record<string, number> }>(
    `/collections/product-counts${query ? `?${query}` : ''}`
  );
}

export type MainMenuSlotOption = {
  id: string;
  title: string;
  depth: number;
  depthLabel: string;
};

export async function getMainMenuSlots() {
  return fetchApi<{
    slots: MainMenuSlotOption[];
    menuTitle: string;
    menuHandle: string;
  }>('/collections/main-menu/slots');
}

export async function patchCollectionMenuMapping(
  collectionId: string,
  body: {
    menu_sync_enabled?: boolean;
    menu_parent_item_id?: string | null;
    menu_sync_excluded?: boolean;
    menu_url_override?: string | null;
  }
) {
  return fetchApi<{ ok: boolean }>(
    `/collections/${encodeURIComponent(collectionId)}/menu-mapping`,
    {
      method: 'PATCH',
      body: JSON.stringify(body),
    }
  );
}

export async function fixCollectionPathCache() {
  return fetchApi<{ ok: boolean; collection_count: number }>('/collections/fix-path-cache', {
    method: 'POST',
    body: '{}',
  });
}

export async function pushMainMenu() {
  return fetchApi<{ ok: boolean }>('/collections/main-menu/push', {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export async function patchCollectionTree(
  collectionId: string,
  body: { parent_id?: string | null; sort_order?: number; in_hierarchy?: boolean }
) {
  return fetchApi<{ ok: boolean; depth?: number }>(
    `/collections/${encodeURIComponent(collectionId)}/tree`,
    {
      method: 'PATCH',
      body: JSON.stringify(body),
    }
  );
}

export async function sortCollectionChildrenAlphabetically(collectionId: string) {
  return fetchApi<{ ok: boolean; updated: number }>(
    `/collections/${encodeURIComponent(collectionId)}/sort-children-alphabetically`,
    {
      method: 'POST',
      body: JSON.stringify({}),
    }
  );
}

export type CollectionProductDraftOverlay = {
  pending_assign_product_ids: string[];
  pending_unassign_product_ids: string[];
};

export interface CollectionDraftProductPreview {
  id: string;
  title: string;
  handle: string | null;
  status: string | null;
  /** Shopify Online Store sales channel (from synced publishedAt / onlineStoreUrl). */
  published_to_online_store?: boolean;
  /** The product's featured image. */
  image_url?: string | null;
}

export type CollectionProductListRow = {
  id: string;
  title: string;
  handle: string | null;
  status: string | null;
  refid?: string | null;
  published_to_online_store?: boolean;
  /** The product's featured image. */
  image_url?: string | null;
};

export interface CollectionDraftProductSummaryRow {
  collection_id: string;
  pending_assign_count: number;
  pending_unassign_count: number;
  pending_assign_preview: CollectionDraftProductPreview[];
  pending_unassign_preview: CollectionDraftProductPreview[];
}

export async function getCollectionProducts(
  collectionId: string,
  params?: {
    page?: number;
    limit?: number;
    q?: string;
    include_draft?: boolean;
    /** Product statuses to list (manual collections only), as the tree's counts use. */
    statuses?: string[];
  }
) {
  const q = new URLSearchParams();
  if (params?.page) q.set('page', String(params.page));
  if (params?.limit) q.set('limit', String(params.limit));
  if (params?.q) q.set('q', params.q);
  if (params?.include_draft === false) q.set('include_draft', 'false');
  if (params?.statuses?.length) q.set('statuses', params.statuses.join(','));
  const qs = q.toString();
  return fetchApi<{
    products: CollectionProductListRow[];
    page: number;
    limit: number;
    total: number;
    has_next: boolean;
    draft?: CollectionProductDraftOverlay;
  }>(`/collections/${encodeURIComponent(collectionId)}/products${qs ? `?${qs}` : ''}`);
}

async function getCollectionMembershipGapProducts(
  collectionId: string,
  pathSuffix: 'missing-descendant-products' | 'products-not-in-children',
  params?: { page?: number; limit?: number; q?: string }
) {
  const q = new URLSearchParams();
  if (params?.page) q.set('page', String(params.page));
  if (params?.limit) q.set('limit', String(params.limit));
  if (params?.q) q.set('q', params.q);
  const qs = q.toString();
  return fetchApi<{
    products: CollectionProductListRow[];
    page: number;
    limit: number;
    total: number;
    has_next: boolean;
  }>(
    `/collections/${encodeURIComponent(collectionId)}/${pathSuffix}${qs ? `?${qs}` : ''}`
  );
}

export async function getMissingDescendantCollectionProducts(
  collectionId: string,
  params?: { page?: number; limit?: number; q?: string }
) {
  return getCollectionMembershipGapProducts(collectionId, 'missing-descendant-products', params);
}

/** Products on this collection that are not in any direct child. */
export async function getCollectionProductsNotInChildren(
  collectionId: string,
  params?: { page?: number; limit?: number; q?: string }
) {
  return getCollectionMembershipGapProducts(collectionId, 'products-not-in-children', params);
}

export type CollectionMembershipGapRow = {
  id: string;
  title: string;
  handle: string | null;
  fullname: string | null;
  parent_id: string | null;
  is_smart: boolean;
  in_children_not_here_count: number;
  here_not_in_direct_children_count: number;
};

export type CollectionMembershipGapTotals = {
  collections_with_gaps: number;
  in_children_not_here: number;
  here_not_in_direct_children: number;
};

export async function getCollectionMembershipGaps() {
  return fetchApi<{
    collections: CollectionMembershipGapRow[];
    totals: CollectionMembershipGapTotals;
  }>('/collections/membership-gaps');
}

export async function getCollectionDraftProductSummary() {
  return fetchApi<{ collections: CollectionDraftProductSummaryRow[] }>(
    '/collections/draft/product-summary'
  );
}

export async function getCollectionAssignCascade(collectionId: string) {
  return fetchApi<{ targets: { id: string; title: string }[] }>(
    `/collections/${encodeURIComponent(collectionId)}/assign-cascade`
  );
}

export async function browseCollectionPickerProducts(params?: { page?: number; limit?: number; q?: string }) {
  const q = new URLSearchParams();
  if (params?.page) q.set('page', String(params.page));
  if (params?.limit) q.set('limit', String(params.limit));
  if (params?.q) q.set('q', params.q);
  const qs = q.toString();
  return fetchApi<{
    products: {
      id: string;
      title: string;
      status: string | null;
      collection_names: string[];
    }[];
    page: number;
    limit: number;
    total: number;
  }>(`/collections/browse-products${qs ? `?${qs}` : ''}`);
}

export async function getUnassignedCollectionProducts(params?: {
  page?: number;
  limit?: number;
  q?: string;
  /** When set, products not linked to this collection or any descendant in the tree. */
  collectionId?: string;
}) {
  const q = new URLSearchParams();
  if (params?.page) q.set('page', String(params.page));
  if (params?.limit) q.set('limit', String(params.limit));
  if (params?.q) q.set('q', params.q);
  if (params?.collectionId) q.set('collection_id', params.collectionId);
  const qs = q.toString();
  return fetchApi<{
    products: CollectionProductListRow[];
    page: number;
    limit: number;
    total: number;
  }>(`/collections/unassigned-products${qs ? `?${qs}` : ''}`);
}

export interface CollectionChangeRow {
  id: string;
  position: number;
  action_type: string;
  payload: Record<string, unknown>;
  client_ref: string | null;
  created_at?: string;
}

export async function discardCollectionDraft() {
  return fetchApi<{ ok: boolean }>('/collections/draft/discard', { method: 'POST', body: '{}' });
}

export async function rebaseCollectionDraft() {
  return fetchApi<{ ok: boolean; based_on_sync_at: string | null }>('/collections/draft/rebase', {
    method: 'POST',
    body: '{}',
  });
}

export async function draftAssignCollectionProductsBatch(
  items: { collection_id: string; product_ids: string[] }[]
) {
  return fetchApi<{ ok: boolean; change_set_id: string; count?: number }>(
    '/collections/draft/assign-products-batch',
    {
      method: 'POST',
      body: JSON.stringify({ items }),
    }
  );
}

export async function draftAssignCollectionProducts(collectionId: string, productIds: string[]) {
  return draftAssignCollectionProductsBatch([{ collection_id: collectionId, product_ids: productIds }]);
}

export async function draftUnassignCollectionProductsBatch(
  items: { collection_id: string; product_ids: string[] }[]
) {
  return fetchApi<{ ok: boolean; change_set_id: string; count?: number }>(
    '/collections/draft/unassign-products-batch',
    {
      method: 'POST',
      body: JSON.stringify({ items }),
    }
  );
}

export async function draftUnassignCollectionProducts(collectionId: string, productIds: string[]) {
  return draftUnassignCollectionProductsBatch([{ collection_id: collectionId, product_ids: productIds }]);
}

export async function discardDraftMembershipBatch(
  items: { collection_id: string; product_id: string; kind: 'assign' | 'unassign' }[]
) {
  return fetchApi<{ ok: boolean; applied: number }>('/collections/draft/discard-membership-batch', {
    method: 'POST',
    body: JSON.stringify({ items }),
  });
}

export async function discardDraftMembershipChange(body: {
  collection_id: string;
  product_id: string;
  kind: 'assign' | 'unassign';
}) {
  return discardDraftMembershipBatch([body]);
}

export async function draftConvertSmartCollectionToManual(collectionId: string) {
  return fetchApi<{ ok: boolean; change_set_id: string; product_count: number }>(
    '/collections/draft/convert-smart-to-manual',
    {
      method: 'POST',
      body: JSON.stringify({ collection_id: collectionId }),
    }
  );
}

export async function draftCreateCollection(body: {
  title: string;
  handle?: string;
  parent_id?: string | null;
  sort_order?: number;
  in_hierarchy?: boolean;
  publish_after_commit?: boolean;
  client_ref?: string;
}) {
  return fetchApi<{ ok: boolean; change_set_id: string; client_ref: string }>('/collections/draft/create', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function draftRenameCollection(
  collectionId: string,
  title: string,
  options?: { handle?: string | null; redirectOldHandle?: boolean }
) {
  const body: Record<string, unknown> = { collection_id: collectionId, title };
  if (options?.handle !== undefined) {
    body.handle = options.handle;
    if (options.redirectOldHandle) body.redirect_old_handle = true;
  }
  return fetchApi<{ ok: boolean; change_set_id: string }>('/collections/draft/rename', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function draftPublishCollection(collectionId: string) {
  return fetchApi<{ ok: boolean; change_set_id: string }>('/collections/draft/publish', {
    method: 'POST',
    body: JSON.stringify({ collection_id: collectionId }),
  });
}

export type SalesChannelPublication = {
  id: string;
  name: string;
  is_online_store: boolean;
};

export async function getCollectionPublications() {
  return fetchApi<{ publications: SalesChannelPublication[] }>('/collections/publications');
}

export async function draftSetCollectionPublications(
  collectionId: string,
  publicationIds: string[],
  isPublished: boolean
) {
  return fetchApi<{ ok: boolean; change_set_id: string }>('/collections/draft/set-publications', {
    method: 'POST',
    body: JSON.stringify({
      collection_id: collectionId,
      publication_ids: publicationIds,
      is_published: isPublished,
    }),
  });
}

export async function draftDeleteCollection(collectionId: string) {
  return fetchApi<{ ok: boolean; change_set_id: string }>('/collections/draft/delete', {
    method: 'POST',
    body: JSON.stringify({ collection_id: collectionId }),
  });
}

// --- Product content drafts (SEO) ---

export async function getProductContentDraftSummary() {
  return fetchApi<ProductContentDraftSummaryResponse>('/product-content/draft/summary');
}

export async function getProductContentDraft() {
  return fetchApi<ProductContentDraftResponse>('/product-content/draft');
}

export async function stageProductSeo(body: StageProductSeoRequest) {
  return fetchApi<StageProductSeoResponse>('/product-content/draft/product-seo', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function stageProductTeaser(body: StageProductTeaserRequest) {
  return fetchApi<StageProductTeaserResponse>(
    '/product-content/draft/product-teaser',
    {
      method: 'POST',
      body: JSON.stringify(body),
    }
  );
}

export async function stageProductDescription(body: StageProductDescriptionRequest) {
  return fetchApi<StageProductDescriptionResponse>('/product-content/draft/product-description', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function stageProductType(body: StageProductTypeRequest) {
  return fetchApi<StageProductTypeResponse>(
    '/product-content/draft/product-type',
    {
      method: 'POST',
      body: JSON.stringify(body),
    }
  );
}

export type CollectionContentResponse = {
  collection_id: string;
  title: string;
  refid: string;
  is_smart: boolean;
  /** The collection's own picture and handle (none while it's only staged). */
  handle?: string | null;
  image_url?: string | null;
  is_pending_create?: boolean;
  is_pending_delete?: boolean;
  is_pending_convert?: boolean;
  editable: boolean;
  edit_blocked_reason: string | null;
  content: CollectionContentPayload;
  committed: CollectionContentPayload;
  pending: ProductContentPendingCollectionContent | null;
};

export async function getCollectionContent(collectionId: string) {
  return fetchApi<CollectionContentResponse>(
    `/collections/${encodeURIComponent(collectionId)}/content`
  );
}

export async function stageCollectionContent(body: StageCollectionContentRequest) {
  return fetchApi<StageCollectionContentResponse>(
    '/product-content/draft/collection-content',
    {
      method: 'POST',
      body: JSON.stringify(body),
    }
  );
}

export async function discardProductContentChange(changeId: string) {
  return fetchApi<DiscardProductContentResponse>(
    `/product-content/draft/changes/${encodeURIComponent(changeId)}`,
    { method: 'DELETE' }
  );
}

export async function discardProductContentDraft() {
  return fetchApi<DiscardProductContentResponse>('/product-content/draft/discard', {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export async function commitProductContentChanges(changeIds?: string[]) {
  return fetchApi<ProductContentCommitStartResponse>('/product-content/commit', {
    method: 'POST',
    body: JSON.stringify(
      changeIds && changeIds.length > 0 ? { change_ids: changeIds } : {}
    ),
  });
}

export async function getProductContentCommitStatus(changeSetId: string) {
  const qs = new URLSearchParams({ change_set_id: changeSetId }).toString();
  return fetchApi<ProductContentCommitStatusResponse>(
    `/product-content/commit-status?${qs}`
  );
}

// Function to get workflow history (paginated)
export async function getWorkflowHistory(params?: {
  page?: number;
  limit?: number;
  /**
   * Cloudflare status refresh (slow).
   * - true: refresh active workflows only (polling / after trigger)
   * - 'full': also backfill missing error details (initial load / Refresh button)
   */
  refresh?: boolean | 'full';
  /** A run's workflow_id, answered as `followed` whichever page is asked for. */
  follow?: string;
}) {
  const qs = new URLSearchParams();
  if (params?.page != null) qs.set('page', String(params.page));
  if (params?.limit != null) qs.set('limit', String(params.limit));
  if (params?.refresh === 'full') qs.set('refresh', 'full');
  else if (params?.refresh) qs.set('refresh', '1');
  if (params?.follow) qs.set('follow', params.follow);
  const query = qs.toString();
  return fetchApi<WorkflowHistoryResponse>(`/sync/history${query ? `?${query}` : ''}`, {
    method: 'GET',
  });
}
